/* cali 2026-09-29 kit upsell: the in-house "Complete your kit" carousel.
 *
 * TWO HALVES IN ONE FILE
 * ----------------------
 * 1. A PURE CORE (no DOM, no globals at load) that mirrors the HQ reference
 *    implementation lib/kit-upsell/facts.ts line for line: cartFacts,
 *    pickSegment, orderedOffers, preselectColour. Both are tested against the
 *    same fixture file (tools/kit-upsell/fixtures.json here, a byte-identical
 *    copy of lib/kit-upsell/fixtures.json in HQ), so the storefront and the HQ
 *    simulator cannot drift without a test failing. Loadable in Node
 *    (module.exports) for tools/kit-upsell/test-kit-upsell.mjs, and exposed in
 *    the browser as window.CaliKitUpsell.core.
 * 2. THE WIDGET, which only runs when `document` exists: reads the JSON that
 *    snippets/cali-kit-upsell.liquid renders into each mount (drawer and/or cart
 *    page), keys everything off the CART (/cart.js, never the DOM), and renders
 *    a carousel of up to max_cards cards in segment order.
 *
 * RULES THAT MUST NOT MOVE (docs/HANDOFF-KIT-UPSELL.md §2, §3, §6 in HQ)
 *  - Prices come from Liquid. A single product shows its Liquid `| money`
 *    string; a two-part card shows the SUM of two Liquid prices, formatted with
 *    the server-rendered money sample (the cali-cart-sync.js technique). The JS
 *    never computes a price of its own.
 *  - Line properties are the analytics contract: _cali_upsell=<pool key>,
 *    _cali_ku_segment=<segment key>, _cali_ku_arm=<arm>.
 *  - After Add: the card leaves, the next eligible one takes its place, the
 *    carousel scrolls back to the start. No "Added" state, no Undo. Never
 *    re-ranks while a pointer is down on the carousel.
 *  - No dismissing (owner, 7 Oct 2026): every eligible card stays until it is
 *    added. An add-on removed from the cart becomes eligible again (it simply
 *    leaves in_cart). The name and photo open a product preview over the cart.
 *  - Fails closed: any error hides the widget and restores the third-party one;
 *    the drawer itself is never touched.
 *  - Every customer-visible string comes from the Liquid JSON.
 *
 * SPLIT (config.split, shape from lib/kit-upsell/config.ts `Split`):
 *   { mode: 'off' }                      everyone is treatment
 *   { mode: 'cart_token', pct: 0..100 }  bucket(cart token) < pct -> treatment
 *   { mode: 'country', countries: [..] } visitor country in list -> treatment
 * bucket = FNV-1a 32-bit over the cart token's UTF-16 code units (the part
 * before any "?", so the Liquid and /cart.js spellings of one token agree),
 * then the murmur3 finaliser, then % 100. Stable across page loads for one
 * cart; a new cart can land in the other arm.
 *
 * EVENTS (window.dataLayer): cali_upsell_impression | cali_upsell_accept |
 * cali_upsell_preview | cali_upsell_preview_link with upsell_offer,
 * upsell_segment, upsell_position (1-based), upsell_arm, upsell_value (major
 * units), upsell_currency, upsell_location; accept adds upsell_variants.
 * cali_upsell_decline is gone with the dismiss button (7 Oct 2026).
 * cali_upsell_extra_open (10 Oct 2026): the extra-strap card was tapped (it
 * opens the builder; the builder reports the accept with source 'cart'), with
 * upsell_extra_kind; its impressions use upsell_offer 'extra-strap'.
 * The control arm sends ONE impression per session and surface, with
 * upsell_arm 'control' and no offer, when the drawer opens (cart page: on load)
 * while the treatment would have had cards to show.
 *
 * Debug: append ?cali_ku_debug=1 to any URL.
 */
(function (factory) {
  'use strict';
  var core = factory();
  if (typeof module === 'object' && module && module.exports) module.exports = core;
  if (typeof window !== 'undefined') {
    window.CaliKitUpsell = window.CaliKitUpsell || {};
    window.CaliKitUpsell.core = core;
  }
})(function () {
  'use strict';

  /* ---------------------------------------------------------------------
     CORE. Mirrors lib/kit-upsell/facts.ts (HQ). Keep the two in step.
     --------------------------------------------------------------------- */

  /* /cart.js lists the most recently added line FIRST. */
  var NEWEST_FIRST = true;

  var DEFAULT_SKU_PREFIXES = {
    'flat-lite': ['gear-module-strap-flat-lite'],
    'flat-alpine': ['gear-module-strap-flat-alp'],
    rope: ['gear-module-strap-rope-'],
    carabiner: ['gear-module-carabiner-'],
    phone: ['gear-module-phonestrapadapter-'],
    stab: ['gear-module-stab-'],
    flask: ['gear-flask'],
    bike: ['gear-module-handlebarkit-'],
    cube: ['gear-cube-'],
    'cube-pro': ['gear-cubepro-'],
    emergency: ['gear-emergblnkt']
  };

  var PROTECTION_PREFIX = 'nvdprotection';
  var PROTECTION_HANDLE = 'navidium-shipping-protection';

  var ROPE_COLOURS = {
    black: 'black',
    citrus: 'citrus',
    // Sold as Optical Purple since 15 Aug 2026; the SKU kept its old name.
    crimson: 'optical purple',
    orangecoal: 'orange coal',
    yellemb: 'yellow ember'
  };

  var BODY_COLOURS = {
    black: 'black dura-tx',
    gridblack: 'gridstop black',
    gridblue: 'gridstop blue',
    gridchili: 'gridstop chili',
    griddune: 'gridstop dune',
    gridoliv: 'gridstop olive',
    gridolive: 'gridstop olive',
    lim: 'lime',
    ultrav: 'ultraviolet'
  };

  function has(obj, key) {
    return !!obj && Object.prototype.hasOwnProperty.call(obj, key);
  }

  function isArray(v) {
    return Object.prototype.toString.call(v) === '[object Array]';
  }

  function lower(v) {
    return typeof v === 'string' ? v.toLowerCase() : '';
  }

  function startsWith(s, prefix) {
    return s.slice(0, prefix.length) === prefix;
  }

  function prop(item, name) {
    var p = item.properties;
    if (!p || typeof p !== 'object') return null;
    var v = p[name];
    return typeof v === 'string' && v !== '' ? v : null;
  }

  function isProtectionLine(item) {
    return startsWith(lower(item.sku), PROTECTION_PREFIX) || lower(item.handle) === PROTECTION_HANDLE;
  }

  function isIgnoredLine(item) {
    return isProtectionLine(item) || item.gift_card === true;
  }

  function sizeOfSku(sku) {
    var s = sku.toLowerCase();
    if (s.indexOf('gogo2l') !== -1 || startsWith(s, 'gear-bundle-2l') || startsWith(s, 'virtual-sling-medium')) return 'medium';
    if (s.indexOf('gogo5l') !== -1 || startsWith(s, 'gear-bundle-5l') || startsWith(s, 'virtual-sling-large')) return 'large';
    return null;
  }

  /* A carabiner never implies a rope: only a `strap-rope` SKU does. */
  function strapOfSku(sku, into) {
    var s = sku.toLowerCase();
    if (s.indexOf('strap-rope') !== -1) into.rope = true;
    if (s.indexOf('strap-flat-lite') !== -1) into.lite = true;
    if (s.indexOf('strap-flat-alp') !== -1) into.alpine = true;
  }

  /* cali 2026-10-07 kit upsell: one strap line is one carry (carabiners never count). */
  function isCarrySku(sku) {
    var s = sku.toLowerCase();
    return s.indexOf('strap-rope') !== -1 || s.indexOf('strap-flat-lite') !== -1 || s.indexOf('strap-flat-alp') !== -1;
  }

  function strapOf(set) {
    var flat = set.lite || set.alpine;
    if (set.rope && flat) return 'both';
    if (set.rope) return 'rope';
    if (set.alpine) return 'flat-alpine';
    if (set.lite) return 'flat-lite';
    return 'none';
  }

  function ropeColourOfSku(sku) {
    var m = /strap-rope-8mm([a-z0-9]+)/.exec(sku.toLowerCase());
    if (!m) return null;
    return has(ROPE_COLOURS, m[1]) ? ROPE_COLOURS[m[1]] : m[1];
  }

  function bodyColourOfSku(sku) {
    var m = /gogo[25]l-([a-z0-9]+)/.exec(sku.toLowerCase());
    if (!m) return null;
    return has(BODY_COLOURS, m[1]) ? BODY_COLOURS[m[1]] : m[1];
  }

  function isBagSku(sku) {
    var s = sku.toLowerCase();
    return startsWith(s, 'virtual-sling-') || startsWith(s, 'gear-bundle-') || startsWith(s, 'gear-module-bag-');
  }

  function defaultPoolIdentities() {
    var out = [];
    for (var key in DEFAULT_SKU_PREFIXES) {
      if (has(DEFAULT_SKU_PREFIXES, key)) out.push({ key: key, sku_prefixes: DEFAULT_SKU_PREFIXES[key] });
    }
    return out;
  }

  /* The pool key of a SKU (longest prefix wins), else of a product handle. */
  function matchPoolKey(sku, handle, pool) {
    var s = (sku || '').toLowerCase();
    var best = null;
    var i, j;
    if (s) {
      for (i = 0; i < pool.length; i++) {
        var prefixes = pool[i].sku_prefixes || [];
        for (j = 0; j < prefixes.length; j++) {
          var prefix = String(prefixes[j]).toLowerCase();
          if (prefix && startsWith(s, prefix) && (!best || prefix.length > best.len)) {
            best = { key: pool[i].key, len: prefix.length };
          }
        }
      }
    }
    if (best) return best.key;
    var h = (handle || '').toLowerCase();
    if (h) {
      for (i = 0; i < pool.length; i++) {
        if ((pool[i].handle || '').toLowerCase() === h) return pool[i].key;
      }
    }
    return null;
  }

  function emptyStraps() {
    return { rope: false, lite: false, alpine: false };
  }

  /* /cart.js items -> facts. See lib/kit-upsell/facts.ts `cartFacts`. */
  function cartFacts(items, opts) {
    opts = opts || {};
    var pool = opts.pool || defaultPoolIdentities();
    var expand = {};
    var src = opts.expandByHandle || {};
    for (var h in src) {
      if (has(src, h) && isArray(src[h])) {
        expand[h.toLowerCase()] = src[h].filter(function (x) { return typeof x === 'string'; });
      }
    }
    var list = isArray(items) ? items : [];

    var groups = {};
    var groupIds = [];
    var loose = emptyStraps();
    var looseCarries = 0;
    var inCart = {};
    var ropes = [];

    list.forEach(function (item, index) {
      if (!item || isIgnoredLine(item)) return;
      var sku = lower(item.sku);
      var handle = lower(item.handle) || null;
      var position = NEWEST_FIRST ? index : list.length - 1 - index;

      var extra = startsWith(sku, 'gear-bundle-') && handle ? expand[handle] || [] : [];
      var moduleSkus = [sku].concat(extra)
        .map(function (s) { return s.toLowerCase(); })
        .filter(Boolean);

      var anyMatch = false;
      moduleSkus.forEach(function (s) {
        var key = matchPoolKey(s, null, pool);
        if (key) {
          inCart[key] = true;
          anyMatch = true;
        }
        var colour = ropeColourOfSku(s);
        if (colour) ropes.push({ position: position, colour: colour });
      });
      // A paid line on a pool product whose SKU family is unknown is still
      // recognised by its handle.
      if (!anyMatch) {
        var byHandle = matchPoolKey('', handle, pool);
        if (byHandle) inCart[byHandle] = true;
      }

      var bid = prop(item, '_bid');
      if (bid === null) bid = prop(item, '_bundleId');
      var bagLine = isBagSku(sku);
      var groupId = bid ? 'bid:' + bid : bagLine ? 'line:' + index : null;

      if (!groupId) {
        moduleSkus.forEach(function (s) {
          strapOfSku(s, loose);
          if (isCarrySku(s)) looseCarries += 1;
        });
        return;
      }

      var g = groups[groupId];
      if (!g) {
        g = {
          position: position,
          bag: false,
          builder: groupId.slice(0, 4) === 'bid:',
          size: null,
          straps: emptyStraps(),
          carries: 0,
          rope_colour: null,
          body_colour: null
        };
        groups[groupId] = g;
        groupIds.push(groupId);
      }
      g.position = Math.min(g.position, position);

      moduleSkus.forEach(function (s) {
        if (isBagSku(s)) g.bag = true;
        if (g.size === null) g.size = sizeOfSku(s);
        strapOfSku(s, g.straps);
        if (isCarrySku(s)) g.carries += 1;
        if (g.rope_colour === null) g.rope_colour = ropeColourOfSku(s);
        if (g.body_colour === null) g.body_colour = bodyColourOfSku(s);
      });

      // Prebuilt shell with no expand map: the product type names the carry.
      // "Flat Strap Sling Bag" reads as flat-lite (no second carry offered).
      if (startsWith(sku, 'gear-bundle-') && moduleSkus.length === 1) {
        var type = lower(item.product_type);
        if (type.indexOf('rope sling') !== -1) {
          g.straps.rope = true;
          g.carries += 1;
        } else if (type.indexOf('flat strap sling') !== -1) {
          g.straps.lite = true;
          g.carries += 1;
        }
      }
    });

    var all = groupIds.map(function (id) { return groups[id]; });
    var bags = all.filter(function (g) { return g.bag; }).sort(function (a, b) { return a.position - b.position; });
    var decider = bags.length ? bags[0] : null;

    all.forEach(function (g) {
      if (g.bag) return;
      loose.rope = loose.rope || g.straps.rope;
      loose.lite = loose.lite || g.straps.lite;
      loose.alpine = loose.alpine || g.straps.alpine;
      looseCarries += g.carries;
    });

    var straps = {
      rope: loose.rope || (decider ? decider.straps.rope : false),
      lite: loose.lite || (decider ? decider.straps.lite : false),
      alpine: loose.alpine || (decider ? decider.straps.alpine : false)
    };

    ropes.sort(function (a, b) { return a.position - b.position; });
    var ropeColour = decider && decider.rope_colour !== null ? decider.rope_colour : ropes.length ? ropes[0].colour : null;

    var order = {};
    pool.forEach(function (p, i) { if (!has(order, p.key)) order[p.key] = i; });
    var keys = Object.keys(inCart).sort(function (a, b) {
      return (has(order, a) ? order[a] : 999) - (has(order, b) ? order[b] : 999);
    });

    // cali 2026-10-07 kit upsell: two carries (the builder's extra strap, or a
    // paid second strap) read as 'both', so no third carry is ever offered.
    var carries = looseCarries + (decider ? decider.carries : 0);

    // cali 2026-10-10 kit upsell: with no bag, the newest cart line matching a product rule
    // names the rule (/cart.js lists the most recently added line first).
    var rule = null;
    var rules = isArray(opts.productRules) ? opts.productRules : [];
    if (decider === null && rules.length) {
      for (var li = 0; li < list.length && rule === null; li++) {
        var it = NEWEST_FIRST ? list[li] : list[list.length - 1 - li];
        if (!it || isIgnoredLine(it)) continue;
        var s = lower(it.sku);
        if (!s) continue;
        for (var ri = 0; ri < rules.length && rule === null; ri++) {
          var r = rules[ri];
          var prefixes = (r && r.match && isArray(r.match.sku_prefixes)) ? r.match.sku_prefixes : [];
          for (var pi = 0; pi < prefixes.length; pi++) {
            var pref = String(prefixes[pi]).toLowerCase();
            if (pref && startsWith(s, pref)) { rule = r.key; break; }
          }
        }
      }
    }

    return {
      bag: decider !== null,
      // cali 2026-10-09 kit upsell: the deciding bag is a builder bundle (it has a
      // _bid/_bundleId), not a bag bought on its own. Builder carts never get a
      // full-price strap card; a standalone bag still does.
      builder: decider !== null && decider.builder,
      size: decider ? decider.size : null,
      strap: carries >= 2 ? 'both' : strapOf(straps),
      rope_colour: ropeColour,
      body_colour: decider ? decider.body_colour : null,
      in_cart: keys,
      rule: rule
    };
  }

  /* Absent conditions are wildcards. */
  function segmentMatches(segment, facts) {
    var w = (segment && segment.when) || {};
    if (w.bag !== undefined && w.bag !== facts.bag) return false;
    if (w.builder !== undefined && w.builder !== facts.builder) return false;
    if (w.strap !== undefined && w.strap !== facts.strap) return false;
    if (w.size !== undefined && w.size !== facts.size) return false;
    return true;
  }

  /* First matching segment wins. */
  function pickSegment(facts, config) {
    var segments = (config && config.segments) || [];
    for (var i = 0; i < segments.length; i++) {
      if (segmentMatches(segments[i], facts)) return segments[i];
    }
    return null;
  }

  function ruleByKey(config, key) {
    var rules = (config && config.product_rules) || [];
    for (var i = 0; i < rules.length; i++) {
      if (rules[i] && rules[i].key === key) return rules[i];
    }
    return null;
  }

  /* cali 2026-10-10 kit upsell, rule B v2 (see facts.ts `effectiveParts`): the parts a card
     really adds. Lead in the cart or its category dismissed: none (the card is skipped). A
     `with` part in the cart or dismissed: dropped, the rest of the card stays ("rope +
     carabiners" becomes "rope" when the carabiners are already in the cart). */
  function effectiveParts(entry, facts, config, dismissedCategories) {
    var pool = {};
    ((config && config.pool) || []).forEach(function (p) { pool[p.key] = p; });
    var dismissed = {};
    (dismissedCategories || []).forEach(function (c) { dismissed[String(c).toLowerCase()] = true; });
    var inCart = {};
    ((facts && facts.in_cart) || []).forEach(function (k) { inCart[k] = true; });
    var hide = !!(config && config.hide_if_in_cart);
    var out = function (key) {
      var cat = has(pool, key) ? pool[key].category : key === entry.key ? entry.category : null;
      return (hide && inCart[key]) || (cat != null && dismissed[String(cat).toLowerCase()]);
    };
    if (out(entry.key)) return [];
    return offerParts(entry).filter(function (k, i) { return i === 0 || !out(k); });
  }

  /* The pool keys to render, in order (see facts.ts `orderedOffers`). */
  function orderedOffers(facts, config, dismissedCategories) {
    if (!config.enabled) return [];
    var segment = pickSegment(facts, config);
    // cali 2026-10-10 kit upsell: an accessory cart whose newest matching line has its own
    // product rule shows that rule's order first, then the default (segment) order.
    var rule = facts && facts.rule ? ruleByKey(config, facts.rule) : null;
    if (!segment && !rule) return [];
    var pool = {};
    (config.pool || []).forEach(function (p) { pool[p.key] = p; });
    var order = (rule ? rule.order || [] : []).concat(segment ? segment.order || [] : []);
    var out = [];
    for (var i = 0; i < order.length; i++) {
      var key = order[i];
      if (out.length >= config.max_cards) break;
      if (!has(pool, key)) continue;
      if (out.indexOf(key) !== -1) continue;
      if (!effectiveParts(pool[key], facts, config, dismissedCategories).length) continue;
      out.push(key);
    }
    return out;
  }

  /* The analytics label stamped in _cali_ku_segment: the product rule when one applied. */
  function offerSourceKey(facts, segment) {
    if (facts && facts.rule) return 'rule:' + facts.rule;
    return segment ? segment.key : null;
  }

  /* ---- extra strap from the cart ----
     cali 2026-10-10 kit upsell (owner): every extra strap on a bag is 20% off.
     The newest builder bag with one strap of its own gets the "Fancy another
     strap?" block; Add writes the extra into the cart the way the builder does.
     One extra keeps the version 1 cart lines; more use version 2 (slots,
     `_extraIndex` on each line; labomono-live-store
     experiments/extra-strap-offer/MULTI-EXTRA-CONTRACT.md). The owner removed the
     limit of three: MAX_EXTRAS is a technical ceiling, matching the Function's.
     The pricing Function prices every extra; nothing here is charged. */

  var MAX_EXTRAS = 20;
  var EXTRA_ROLES = { alpine: 'strap', lite: 'strap', rope: 'rope,carabiner' };

  function variantNum(gid) {
    var m = /^gid:\/\/shopify\/ProductVariant\/(\d+)$/.exec(typeof gid === 'string' ? gid : '');
    return m ? Number(m[1]) : 0;
  }

  /* The extras a bag's master line already carries, oldest first, as
     [{ kind, components: [{ role, id }] }]: [] for none, null when the
     metadata is malformed (such a bag is left alone). Version 1 or 2. */
  function bagExtras(master) {
    var version = prop(master, '_extraOfferVersion');
    var raw = prop(master, '_extraStrap');
    if (!version && !raw) return [];
    if (!version || !raw) return null;
    var data;
    try { data = JSON.parse(raw); } catch (e) { return null; }
    var list;
    if (version === '1' && data && data.version === 1) list = [data];
    else if (version === '2' && data && data.version === 2 && isArray(data.extras)) list = data.extras;
    else return null;
    if (!list.length || list.length > MAX_EXTRAS) return null;
    var out = [];
    for (var i = 0; i < list.length; i++) {
      var x = list[i];
      if (!x || !has(EXTRA_ROLES, x.kind) || !isArray(x.components)) return null;
      var comps = x.components.map(function (c) { return { role: c && c.role, id: variantNum(c && c.variantId) }; });
      if (comps.map(function (c) { return c.role; }).join(',') !== EXTRA_ROLES[x.kind]) return null;
      for (var c = 0; c < comps.length; c++) if (!comps[c].id) return null;
      out.push({ kind: x.kind, components: comps });
    }
    return out;
  }

  /* Mirrors orderedExtraKinds in labomono-live-store
     customization-app/src/cali/extra-strap.js: the family most distinct from the
     bag's own strap leads. */
  function orderedExtraKinds(mainKind) {
    if (mainKind === 'rope') return ['alpine', 'lite', 'rope'];
    if (mainKind === 'lite') return ['rope', 'alpine', 'lite'];
    if (mainKind === 'alpine') return ['rope', 'lite', 'alpine'];
    return ['rope', 'alpine', 'lite'];
  }

  function appendParam(url, name, value) {
    return url + (url.indexOf('?') === -1 ? '?' : '&') + name + '=' + encodeURIComponent(value);
  }

  /* The edit link of a builder master line, built like snippets/cart-item.liquid
     (`cali_bag_edit_url`): design page, design id, then the bag's bundle id. */
  function bagEditUrl(item) {
    var url = prop(item, '_designProductUrl') || (typeof item.url === 'string' ? item.url : '');
    if (!url) return null;
    var designId = prop(item, '_designId') || prop(item, '_designProductId');
    if (designId && url.indexOf('designId=') === -1) url = appendParam(url, 'designId', designId);
    var bid = prop(item, '_bid');
    if (bid && prop(item, '_bundleType') === 'master') url = appendParam(url, 'editBid', bid);
    return url;
  }

  /* A product link in the visitor's current language: the bag keeps the page
     it was added from (`/fr/products/...`), the visitor may have switched since.
     `root` is the locale root of this page ('/' or '/fr/'). Other links pass. */
  function localisedProductUrl(url, root) {
    var r = typeof root === 'string' && root.charAt(0) === '/' ? root : '/';
    if (r.charAt(r.length - 1) !== '/') r += '/';
    var m = /^\/(?:[a-z]{2}(?:-[a-z0-9]{2,4})?\/)?(products\/.*)$/i.exec(url);
    return m ? r + m[1] : url;
  }

  /* cali 2026-10-10 (owner): the rope and carabiner the offer opens on. `values` are listed in the
     owner's order of preference (rope: black, orange, purple, citrus, yellow; carabiner: black,
     orange); the first whose SKU is not already in the cart is the one shown, so a customer with
     a black rope is shown orange. When every colour is already there, the first. */
  function preferredPart(values, items) {
    if (!values || !values.length) return 0;
    var owned = {};
    (items || []).forEach(function (it) {
      var sku = lower(it && it.sku);
      if (sku) owned[sku] = true;
    });
    for (var i = 0; i < values.length; i++) {
      if (!owned[lower(values[i] && values[i].sku)]) return i;
    }
    return 0;
  }

  /* The bag the extra-strap block is for, or null: newest line first, a builder
     master (medium or large) with one carry of its own at most and fewer than
     MAX_EXTRAS extras. `extras` lists the ones it already has.
     opts.root: the locale root the edit link is rewritten to (default: unchanged). */
  function extraStrapTarget(items, opts) {
    opts = opts || {};
    var list = isArray(items) ? items : [];
    var groups = {};
    var i;
    for (i = 0; i < list.length; i++) {
      var g = list[i];
      if (!g || typeof g !== 'object') continue;
      var gid = prop(g, '_bid');
      if (gid) (groups[gid] = groups[gid] || []).push(g);
    }
    for (i = 0; i < list.length; i++) {
      var it = NEWEST_FIRST ? list[i] : list[list.length - 1 - i];
      if (!it || typeof it !== 'object') continue;
      if (prop(it, '_bundleType') !== 'master') continue;
      var size = sizeOfSku(typeof it.sku === 'string' ? it.sku : '');
      var bid = prop(it, '_bid');
      if (!bid || !size) continue;
      var extras = bagExtras(it);
      if (!extras || extras.length >= MAX_EXTRAS) continue;
      var straps = emptyStraps();
      var carries = 0;
      var extraLines = 0;
      var members = groups[bid] || [];
      for (var m = 0; m < members.length; m++) {
        var child = members[m];
        if (child === it) continue;
        if (isExtraLine(child)) { extraLines += 1; continue; }
        var sku = typeof child.sku === 'string' ? child.sku : '';
        if (!sku) continue;
        if (isCarrySku(sku)) carries += 1;
        strapOfSku(sku, straps);
      }
      // Exactly one strap of its own: the pricing Function discounts an extra only
      // on a bag + flat strap or bag + rope + carabiners build, so a bag-only build
      // (no strap) is never offered one (it would be charged in full at checkout).
      // A bag whose extra lines do not match its own list is left alone.
      if (carries !== 1 || extraLines !== extraPartCount(extras)) continue;
      var url = bagEditUrl(it);
      if (url && opts.root) url = localisedProductUrl(url, opts.root);
      var main = straps.rope ? 'rope' : straps.alpine ? 'alpine' : straps.lite ? 'lite' : null;
      return {
        bid: bid,
        size: size,
        main_kind: main,
        extras: extras,
        url: url ? appendParam(appendParam(url, 'editExtra', '1'), 'from', 'cart') : null
      };
    }
    return null;
  }

  /* The kind the card shows: the builder's order for this bag, first one on sale. */
  function extraStrapLead(target, kinds) {
    if (!target || !kinds) return null;
    var order = orderedExtraKinds(target.main_kind);
    for (var i = 0; i < order.length; i++) {
      var k = kinds[order[i]];
      if (k && k.available) return order[i];
    }
    return null;
  }

  function extraPartCount(extras) {
    return extras.reduce(function (n, x) { return n + x.components.length; }, 0);
  }

  function isExtraLine(line) {
    return prop(line, '_bundleType') === 'child' &&
      !!(prop(line, '_extraRole') || prop(line, '_extraOfferVersion') || prop(line, '_extraIndex'));
  }

  /* The cart write that gives bag `bid` one more extra strap in the cart (owner,
     10 Oct: "choose, right there, which strap"), exactly as the builder writes it
     (labomono-live-store BagCustomizer). The master lists every extra in
     `_extraStrap` with standalone variant gids; each component is a child line with
     `_extraRole`, `_extraKind` and `_extraOfferVersion`. One extra: version 1,
     unchanged. Two or three: version 2, slots 1..n, and `_extraIndex` on each child
     so two identical straps stay separate lines. The extras already on the bag are
     written again from its own metadata. Like the builder, the whole group is added
     again under a new bundle id and the old lines are removed after; the pricing
     Function prices the extras, nothing here is charged. `selection` is
     { kind, components: [{ role, id }] } with numeric variant ids. Returns null when
     the bag is gone, already has MAX_EXTRAS extras or unreadable extra metadata, or
     the selection is malformed. */
  function extraStrapItems(items, bid, selection, newBid) {
    var list = isArray(items) ? items : [];
    if (!bid || !newBid || !selection) return null;
    var kind = selection.kind;
    var comps = isArray(selection.components) ? selection.components : [];
    var roles = comps.map(function (c) { return c && c.role; }).join(',');
    if ((kind === 'alpine' || kind === 'lite') ? roles !== 'strap' : kind === 'rope' ? roles !== 'rope,carabiner' : true) return null;
    for (var c = 0; c < comps.length; c++) {
      if (!(Number(comps[c].id) > 0)) return null;
    }
    var group = [];
    var master = null;
    for (var i = 0; i < list.length; i++) {
      var it = list[i];
      if (!it || typeof it !== 'object' || prop(it, '_bid') !== bid) continue;
      group.push(it);
      if (prop(it, '_bundleType') === 'master') master = it;
    }
    if (!master) return null;
    var existing = bagExtras(master);
    if (!existing || existing.length >= MAX_EXTRAS) return null;
    if (group.filter(isExtraLine).length !== extraPartCount(existing)) return null;
    var ownStraps = group.filter(function (line) {
      return line !== master && !isExtraLine(line) && isCarrySku(typeof line.sku === 'string' ? line.sku : '');
    }).length;
    if (ownStraps !== 1) return null;
    var all = existing.concat([{ kind: kind, components: comps.map(function (x) { return { role: x.role, id: Number(x.id) }; }) }]);
    var version = all.length === 1 ? '1' : '2';
    var q = Math.max(1, Math.floor(Number(master.quantity) || 1));
    var copy = function (props) {
      var out = {};
      if (props && typeof props === 'object') {
        for (var k in props) if (has(props, k)) out[k] = props[k];
      }
      return out;
    };
    var gids = function (x) {
      return x.components.map(function (c) { return { role: c.role, variantId: 'gid://shopify/ProductVariant/' + c.id }; });
    };
    var masterProps = copy(master.properties);
    masterProps._bid = newBid;
    masterProps._extraOfferVersion = version;
    masterProps._extraStrap = JSON.stringify(version === '1'
      ? { version: 1, kind: all[0].kind, components: gids(all[0]) }
      : { version: 2, extras: all.map(function (x, n) { return { slot: n + 1, kind: x.kind, components: gids(x) }; }) });
    var out = [{ id: master.variant_id, quantity: q, properties: masterProps }];
    group.forEach(function (line) {
      if (line === master || isExtraLine(line)) return;
      var props = copy(line.properties);
      props._bid = newBid;
      out.push({ id: line.variant_id, quantity: line.quantity, properties: props });
    });
    all.forEach(function (x, n) {
      x.components.forEach(function (c) {
        var props = { _bid: newBid, _bundleType: 'child', _extraRole: c.role, _extraKind: x.kind, _extraOfferVersion: version };
        if (version === '2') props._extraIndex = String(n + 1);
        out.push({ id: c.id, quantity: q, properties: props });
      });
    });
    return { items: out, removeKeys: group.map(function (line) { return line.key; }), quantity: q, count: all.length };
  }

  /* The builder's price rule (snippets/cali-extra-strap-cart.liquid): 80 % of the
     standalone prices, rounded DOWN to a whole currency unit. Minor units. */
  function extraStrapCents(standaloneCents) {
    var c = Math.max(0, Math.floor(Number(standaloneCents) || 0));
    return Math.floor((c * 80) / 10000) * 100;
  }

  /* The option value to pre-select for a pool product, or null (= first available). */
  function preselectColour(key, facts, config) {
    var cm = (config && config.colour_match) || {};
    if (!has(cm, key)) return null;
    var rule = cm[key];
    var map = rule.map || {};
    var source = facts[rule.from];
    if (source && has(map, source.toLowerCase())) return map[source.toLowerCase()];
    return has(map, '*') ? map['*'] : null;
  }

  /* ---------------------------------------------------------------------
     STOREFRONT HELPERS (pure, tested in Node too)
     --------------------------------------------------------------------- */

  var imul = Math.imul || function (a, b) {
    var ah = (a >>> 16) & 0xffff;
    var al = a & 0xffff;
    var bh = (b >>> 16) & 0xffff;
    var bl = b & 0xffff;
    return (al * bl + (((ah * bl + al * bh) << 16) >>> 0)) | 0;
  };

  /* 0..99 from a cart token. FNV-1a 32-bit + murmur3 fmix32, documented above. */
  function tokenBucket(token) {
    var s = String(token || '').split('?')[0];
    var h = 0x811c9dc5;
    for (var i = 0; i < s.length; i++) {
      h ^= s.charCodeAt(i);
      h = imul(h, 0x01000193);
    }
    h ^= h >>> 16;
    h = imul(h, 0x85ebca6b);
    h ^= h >>> 13;
    h = imul(h, 0xc2b2ae35);
    h ^= h >>> 16;
    return (h >>> 0) % 100;
  }

  /* 'treatment' | 'control' | null (null = not decidable yet: no cart token). */
  function splitArm(split, token, country) {
    if (!split || split.mode === 'off' || split.mode === undefined) return 'treatment';
    if (split.mode === 'cart_token') {
      var base = String(token || '').split('?')[0];
      if (!base) return null;
      var pct = typeof split.pct === 'number' ? split.pct : 0;
      return tokenBucket(base) < pct ? 'treatment' : 'control';
    }
    if (split.mode === 'country') {
      var list = isArray(split.countries) ? split.countries : [];
      var c = String(country || '').toUpperCase();
      for (var i = 0; i < list.length; i++) {
        if (String(list[i]).toUpperCase() === c) return 'treatment';
      }
      return 'control';
    }
    // An unknown mode is a document this code does not understand: leave the
    // existing widget alone.
    return 'control';
  }

  /* The pool keys one card adds: the entry itself, then its `with` parts. */
  function offerParts(entry) {
    var parts = [entry.key];
    (entry.with || []).forEach(function (k) {
      if (parts.indexOf(k) === -1) parts.push(k);
    });
    return parts;
  }

  /* The `items` array for ONE atomic /cart/add.js call. */
  function buildAddItems(variantIds, offerKey, segmentKey, arm) {
    return variantIds.map(function (id) {
      return {
        id: id,
        quantity: 1,
        properties: { _cali_upsell: offerKey, _cali_ku_segment: segmentKey, _cali_ku_arm: arm }
      };
    });
  }

  /* Index of the variant to pre-select: case-insensitive match of `value` on the
     variant title or any option value; first variant when nothing matches. */
  function pickVariantIndex(variants, value) {
    if (!variants || !variants.length) return -1;
    if (value) {
      var v = String(value).toLowerCase();
      for (var i = 0; i < variants.length; i++) {
        var opts = variants[i].opts || [];
        if (String(variants[i].title || '').toLowerCase() === v) return i;
        for (var j = 0; j < opts.length; j++) {
          if (String(opts[j]).toLowerCase() === v) return i;
        }
      }
    }
    return 0;
  }

  /* Money formatting from a server-rendered sample of 123456789 minor units
     (same technique as assets/cali-cart-sync.js), so a SUM of Liquid prices
     prints exactly as Liquid's `| money` would. Null when the sample is unusable. */
  function moneyFormatter(sample) {
    if (!sample) return null;
    var m = String(sample).match(/1(\D*)234\D*56[78](\D*89)?/);
    if (!m) return null;
    var pattern = {
      prefix: sample.slice(0, m.index),
      suffix: sample.slice(m.index + m[0].length),
      thousands: m[1] || '',
      decimal: m[2] ? m[2].slice(0, -2) : null
    };
    return function (cents) {
      var n = Math.round(Number(cents) || 0);
      var neg = n < 0;
      n = Math.abs(n);
      var body;
      if (pattern.decimal !== null) {
        var units = String(Math.floor(n / 100));
        var frac = String(n % 100);
        if (frac.length < 2) frac = '0' + frac;
        body = units.replace(/\B(?=(\d{3})+(?!\d))/g, pattern.thousands) + pattern.decimal + frac;
      } else {
        body = String(Math.round(n / 100)).replace(/\B(?=(\d{3})+(?!\d))/g, pattern.thousands);
      }
      return (neg ? '-' : '') + pattern.prefix + body + pattern.suffix;
    };
  }

  return {
    NEWEST_FIRST: NEWEST_FIRST,
    DEFAULT_SKU_PREFIXES: DEFAULT_SKU_PREFIXES,
    cartFacts: cartFacts,
    segmentMatches: segmentMatches,
    pickSegment: pickSegment,
    orderedOffers: orderedOffers,
    effectiveParts: effectiveParts,
    offerSourceKey: offerSourceKey,
    orderedExtraKinds: orderedExtraKinds,
    bagEditUrl: bagEditUrl,
    appendParam: appendParam,
    localisedProductUrl: localisedProductUrl,
    extraStrapTarget: extraStrapTarget,
    preferredPart: preferredPart,
    extraStrapLead: extraStrapLead,
    extraStrapCents: extraStrapCents,
    extraStrapItems: extraStrapItems,
    bagExtras: bagExtras,
    MAX_EXTRAS: MAX_EXTRAS,
    preselectColour: preselectColour,
    matchPoolKey: matchPoolKey,
    tokenBucket: tokenBucket,
    splitArm: splitArm,
    offerParts: offerParts,
    buildAddItems: buildAddItems,
    pickVariantIndex: pickVariantIndex,
    moneyFormatter: moneyFormatter
  };
});

/* -------------------------------------------------------------------------
   WIDGET. Browser only.
   ------------------------------------------------------------------------- */
(function () {
  'use strict';

  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  if (window.__caliKuInit) return;
  window.__caliKuInit = true;

  var core = window.CaliKitUpsell && window.CaliKitUpsell.core;
  if (!core) return;

  var DEBUG = /[?&]cali_ku_debug=1/.test(window.location.search);
  var CART_MUTATION = /\/cart\/(add|change|update|clear)/;
  var SS = { seen: 'caliKuSeen', control: 'caliKuControlSeen' };
  var ERROR_MS = 6000;
  var RERANK_TIMEOUT_MS = 4000;
  var SCROLL_IDLE_MS = 180;

  function log() {
    if (!DEBUG || !window.console) return;
    try {
      var args = Array.prototype.slice.call(arguments);
      args.unshift('[cali-ku]');
      console.log.apply(console, args);
    } catch (err) {}
  }

  /* ---- session memory, fail-soft ---- */
  function ssList(name) {
    try {
      var v = JSON.parse(window.sessionStorage.getItem(name) || '[]');
      return Object.prototype.toString.call(v) === '[object Array]' ? v : [];
    } catch (err) {
      return [];
    }
  }
  function ssPush(name, value) {
    try {
      var list = ssList(name);
      if (list.indexOf(value) === -1) {
        list.push(value);
        window.sessionStorage.setItem(name, JSON.stringify(list));
      }
    } catch (err) {}
  }

  function currency() {
    return (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || '';
  }

  function track(name, data) {
    try {
      var payload = { event: 'cali_upsell_' + name, upsell_currency: currency() };
      for (var k in data) {
        if (Object.prototype.hasOwnProperty.call(data, k)) payload[k] = data[k];
      }
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(payload);
      log(name, payload);
    } catch (err) {}
  }

  function fill(template, vars) {
    return String(template || '').replace(/\[(\w+)\]/g, function (match, key) {
      return Object.prototype.hasOwnProperty.call(vars, key) ? String(vars[key]) : match;
    });
  }

  function stripTags(str) {
    return String(str == null ? '' : str).replace(/<[^>]*>/g, '');
  }

  function rootPath() {
    var r = window.Shopify && window.Shopify.routes && window.Shopify.routes.root;
    return typeof r === 'string' && r ? r : '/';
  }

  function prefersReducedMotion() {
    try {
      return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    } catch (err) {
      return false;
    }
  }

  /* ---- shared state ---- */
  var mounts = [];
  var lastCart = null;
  var cssState = 'idle'; // idle | loading | ready | failed
  var refreshTimer = null;
  var fetchSeq = 0;
  var pendingControlOpen = false;
  var cartReadFailed = false;
  var cardSeq = 0;

  /* ---- stylesheet, attached here so nothing in the page blocks on it ---- */
  function ensureCss(href, done) {
    if (cssState === 'ready') return done(true);
    if (cssState === 'failed') return done(false);
    var cbs = ensureCss.cbs || (ensureCss.cbs = []);
    cbs.push(done);
    if (cssState === 'loading') return;
    cssState = 'loading';
    var finish = function (ok) {
      cssState = ok ? 'ready' : 'failed';
      var list = ensureCss.cbs.splice(0);
      list.forEach(function (cb) { cb(ok); });
    };
    if (!href) return finish(false);
    var link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    link.id = 'CaliKuCss';
    link.onload = function () { finish(true); };
    link.onerror = function () { finish(false); };
    document.head.appendChild(link);
  }

  /* ---- the third-party widget ----
     cali 2026-10-07 kit upsell: while yielding, ours stays hidden whenever the
     third-party app is installed on the page at all, not only while its widget
     is on screen. Motion empties [data-products] (where that widget lives) on
     every rebuild, so "not on screen" happens for a moment after each add, and
     showing ours in that gap made the drawer flash. */
  function thirdPartyPresent() {
    if (window.lbSetup) return true;
    if (document.querySelector('script[src*="selleasy"], lb-upsell-widget, lb-embed')) return true;
    return false;
  }

  function setArmAttribute() {
    // Treatment hides the third-party widget via CSS, but only once ours can
    // really render (stylesheet loaded, config parsed) and only when we are not
    // yielding. Everything else leaves it exactly as it is today.
    var treat = cssState === 'ready' && !cartReadFailed && mounts.some(function (m) {
      return !m.failed && m.arm === 'treatment' && !m.cfg.config.yield_to_selleasy;
    });
    var html = document.documentElement;
    if (treat) html.setAttribute('data-cali-ku-arm', 'treatment');
    else if (html.getAttribute('data-cali-ku-arm') === 'treatment') html.removeAttribute('data-cali-ku-arm');
  }

  function fail(mount, err) {
    mount.failed = true;
    try { hide(mount); } catch (e) {}
    setArmAttribute();
    log('failed', mount.location, err);
  }

  /* ---- config ---- */
  function readMount(root) {
    var el = root.querySelector('[data-cali-ku-config]');
    var cfg = JSON.parse(el.textContent);
    if (!cfg || cfg.v !== 1 || !cfg.config || !cfg.copy || !cfg.products) throw new Error('bad config');
    var c = cfg.config;
    var maxCards = typeof c.max_cards === 'number' ? Math.round(c.max_cards) : 6;
    c.max_cards = Math.min(12, Math.max(1, maxCards));
    c.min_reviews_for_stars = typeof c.min_reviews_for_stars === 'number' ? c.min_reviews_for_stars : 5;
    c.hide_if_in_cart = c.hide_if_in_cart !== false;
    c.yield_to_selleasy = c.yield_to_selleasy !== false;
    c.pool = Object.prototype.toString.call(c.pool) === '[object Array]' ? c.pool : [];
    c.segments = Object.prototype.toString.call(c.segments) === '[object Array]' ? c.segments : [];
    c.colour_match = c.colour_match && typeof c.colour_match === 'object' ? c.colour_match : {};
    c.product_rules = Object.prototype.toString.call(c.product_rules) === '[object Array]' ? c.product_rules : [];
    cfg.strings = cfg.strings || {};
    cfg.expand = cfg.expand || {};
    // cali 2026-10-10: the extra-strap card's data, or null while the builder offer is off.
    cfg.extra = cfg.extra && typeof cfg.extra === 'object' && cfg.extra.kinds && typeof cfg.extra.kinds === 'object' ? cfg.extra : null;

    var format = core.moneyFormatter(cfg.money_sample) ||
      (window.caliCartSync && typeof window.caliCartSync.formatCents === 'function' ? window.caliCartSync.formatCents : null);

    // A card can render only when every part's product is present (rendered by
    // Liquid, i.e. sellable here), and a multi-part card only when its sum can
    // be printed in the storefront's own money format.
    var renderable = c.pool.filter(function (entry) {
      var parts = core.offerParts(entry);
      for (var i = 0; i < parts.length; i++) {
        var p = cfg.products[parts[i]];
        if (!p || !p.variants || !p.variants.length) return false;
      }
      return parts.length === 1 || !!format;
    });

    return {
      root: root,
      location: root.getAttribute('data-cali-ku-location') || cfg.location || 'drawer',
      cfg: cfg,
      track: root.querySelector('[data-cali-ku-track]'),
      nav: root.querySelector('[data-cali-ku-nav]'),
      prev: root.querySelector('[data-cali-ku-prev]'),
      next: root.querySelector('[data-cali-ku-next]'),
      renderablePool: renderable,
      format: format,
      arm: null,
      failed: false,
      cards: [],
      renderedSig: '',
      segmentKey: null,
      offers: [],
      pointerDown: false,
      scrolling: false,
      pendingCart: null,
      adding: false,
      awaitingRerank: false,
      refocus: false,
      scrollToStart: false,
      observer: null
    };
  }

  /* ---- deciding the cards ---- */
  function decide(mount, cart) {
    var c = mount.cfg.config;
    var facts = core.cartFacts(cart.items || [], { pool: c.pool, expandByHandle: mount.cfg.expand, productRules: c.product_rules });
    var segment = core.pickSegment(facts, c);
    var offers = core.orderedOffers(facts, {
      enabled: true,
      segments: c.segments,
      pool: mount.renderablePool,
      hide_if_in_cart: c.hide_if_in_cart,
      max_cards: c.max_cards,
      product_rules: c.product_rules
    }, []);
    log(mount.location, 'facts', facts, 'segment', segment && segment.key, 'offers', offers);
    return { facts: facts, segment: segment, offers: offers };
  }

  function armFor(mount, cart) {
    var token = (cart && cart.token) || (mount.cfg.cart && mount.cfg.cart.token) || '';
    return core.splitArm(mount.cfg.config.split, token, mount.cfg.country);
  }

  /* ---- rendering ---- */
  function render(mount, cart) {
    if (mount.failed || !cart) return;
    // Nothing is shown before the stylesheet is in: start() replays lastCart.
    if (cssState !== 'ready') return;
    if (mount.pointerDown || mount.scrolling || mount.adding) {
      // Never move cards under a finger, mid-swipe, nor mid-add; replay when settled.
      mount.pendingCart = cart;
      return;
    }
    try {
      mount.arm = armFor(mount, cart);
      setArmAttribute();

      var d = decide(mount, cart);
      mount.segmentKey = core.offerSourceKey(d.facts, d.segment);
      mount.lastOffers = d.offers;
      var extra = extraFor(mount, cart);

      if (!mount.arm || cart.item_count === 0 || (!d.offers.length && !extra)) {
        hide(mount);
        return;
      }
      if (mount.arm === 'control') {
        hide(mount);
        maybeControlImpression(mount);
        return;
      }

      if (mount.cfg.config.yield_to_selleasy && thirdPartyPresent()) {
        // Nothing is built, so no impression can fire for a card nobody saw.
        hide(mount);
        return;
      }

      var sig = d.offers.join('|') + '#' + mount.segmentKey;
      if (sig !== mount.renderedSig) {
        // cali 2026-10-07 kit upsell: the rebuild deletes the focused button,
        // so focus goes back to the first card instead of falling to <body>.
        var hadFocus = mount.root.contains(document.activeElement);
        buildCards(mount, d);
        mount.renderedSig = sig;
        if (hadFocus) mount.refocus = true;
      }
      // A card that stayed (hide_if_in_cart off) is usable again after the re-rank.
      mount.cards.forEach(function (card) {
        if (card.els && card.els.add.disabled) {
          card.els.add.disabled = false;
          card.els.add.removeAttribute('aria-busy');
        }
      });

      // cali 2026-10-10: the extra-strap offer sits above the add-on row; with
      // no add-on to show, only it is shown.
      renderExtra(mount, extra);
      mount.root.classList.toggle('cali-ku--no-offers', !d.offers.length);
      showMount(mount);
      if (mount.scrollToStart) {
        mount.scrollToStart = false;
        scrollTrackInstant(mount, 0);
      }
      if (mount.refocus) {
        mount.refocus = false;
        var first = mount.track.querySelector('.cali-ku__add');
        if (first) {
          try { first.focus({ preventScroll: true }); } catch (err) {}
        }
      }
      updateNav(mount);
    } catch (err) {
      fail(mount, err);
    } finally {
      mount.awaitingRerank = false;
    }
  }

  /* The cart page may have reserved our height in Liquid (cali-ku--pending);
     it ends as soon as the script decides either way. */
  function endPending(mount) {
    if (!mount.root.classList.contains('cali-ku--pending')) return;
    mount.root.classList.remove('cali-ku--pending');
    mount.root.removeAttribute('style');
  }

  function showMount(mount) {
    mount.root.hidden = false;
    endPending(mount);
  }

  function hide(mount) {
    mount.root.hidden = true;
    endPending(mount);
  }

  /* Instant, never smooth: the cards were just rebuilt, and a smooth scroll
     (the track's CSS default) is cancelled by the browser re-snapping it. */
  function scrollTrackInstant(mount, left) {
    var t = mount.track;
    var before = t.style.scrollBehavior;
    t.style.scrollBehavior = 'auto';
    t.scrollLeft = left;
    t.style.scrollBehavior = before;
  }

  function scrollTrackTo(mount, left) {
    try {
      mount.track.scrollTo({ left: left, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
    } catch (err) {
      mount.track.scrollLeft = left;
    }
  }

  function el(tag, cls, text) {
    var node = document.createElement(tag);
    if (cls) node.className = cls;
    if (text != null) node.textContent = text;
    return node;
  }

  var SVG_NS = 'http://www.w3.org/2000/svg';
  var STAR_PATH = 'M8 1.2l2.1 4.3 4.7.7-3.4 3.3.8 4.7L8 12l-4.2 2.2.8-4.7L1.2 6.2l4.7-.7z';

  /* A single star, the score and the count (cali 2026-10-10, compact cards). */
  function starIcon() {
    var svg = document.createElementNS(SVG_NS, 'svg');
    svg.setAttribute('viewBox', '0 0 16 16');
    svg.setAttribute('width', '12');
    svg.setAttribute('height', '12');
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('focusable', 'false');
    svg.setAttribute('class', 'cali-ku__star');
    var path = document.createElementNS(SVG_NS, 'path');
    path.setAttribute('d', STAR_PATH);
    svg.appendChild(path);
    return svg;
  }

  function formatScore(n) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || undefined, {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1
      }).format(n);
    } catch (err) {
      return n.toFixed(1);
    }
  }

  function buildRating(mount, product, labelled) {
    var min = mount.cfg.config.min_reviews_for_stars;
    var rating = Number(product.rating) || 0;
    var max = Number(product.rating_max) || 5;
    var count = Number(product.rating_count) || 0;
    if (rating <= 0 || count < min || count <= 0) return null;
    var wrap = el('span', 'cali-ku__rating');
    var shown = el('span', 'cali-ku__rating-shown');
    shown.setAttribute('aria-hidden', 'true');
    shown.appendChild(starIcon());
    shown.appendChild(el('span', 'cali-ku__score', formatScore(Math.round((rating / max) * 50) / 10)));
    shown.appendChild(el('span', 'cali-ku__count', '(' + count + ')'));
    wrap.appendChild(shown);
    var aria = mount.cfg.strings.rating_aria;
    if (aria) {
      var rounded = Math.round(rating * 10) / 10;
      wrap.appendChild(el('span', 'cali-ku__sr', fill(aria, { rating: rounded, max: max, count: count })));
    }
    if (labelled && mount.cfg.strings.rating_for) {
      // Two-part card: the stars are the FIRST product's own rating, said so in
      // the tooltip and to screen readers (no room for the label on the card).
      var ratingFor = fill(mount.cfg.strings.rating_for, { product: product.title });
      wrap.appendChild(el('span', 'cali-ku__sr', ratingFor));
      wrap.title = ratingFor;
    }
    return wrap;
  }

  /* One card's state: parts (pool keys), products, selected variant index per part. */
  function makeCard(mount, key, position, facts) {
    var c = mount.cfg.config;
    var entry = null;
    for (var i = 0; i < c.pool.length; i++) {
      if (c.pool[i].key === key) { entry = c.pool[i]; break; }
    }
    // cali 2026-10-10: only the parts still to add (a two-part card drops a part already in the cart).
    var parts = core.effectiveParts(entry, facts, c, []);
    if (!parts.length) parts = core.offerParts(entry);
    var products = parts.map(function (k) { return mount.cfg.products[k]; });
    var selected = parts.map(function (k, idx) {
      return core.pickVariantIndex(products[idx].variants, core.preselectColour(k, facts, c));
    });
    return {
      key: key,
      entry: entry,
      category: entry.category,
      position: position,
      parts: parts,
      products: products,
      selected: selected,
      node: null,
      errorTimer: null
    };
  }

  function cardVariants(card) {
    return card.products.map(function (p, i) { return p.variants[card.selected[i]]; });
  }

  function cardCents(card) {
    return cardVariants(card).reduce(function (sum, v) { return sum + (Number(v.cents) || 0); }, 0);
  }

  function cardPrice(mount, card) {
    var vs = cardVariants(card);
    if (vs.length === 1) return vs[0].price;
    return mount.format(cardCents(card));
  }

  function buildCards(mount, d) {
    var track = mount.track;
    closeMenu(mount, false);
    if (mount.observer) {
      try { mount.observer.disconnect(); } catch (err) {}
    }
    mount.cards.forEach(function (card) { if (card.errorTimer) clearTimeout(card.errorTimer); });
    while (track.firstChild) track.removeChild(track.firstChild);
    mount.cards = d.offers.map(function (key, i) {
      var card = makeCard(mount, key, i + 1, d.facts);
      card.node = buildCardNode(mount, card);
      track.appendChild(card.node);
      return card;
    });
    observeImpressions(mount);
  }

  /* ---- "Fancy another strap?" (cali 2026-10-10, owner) ----
     Above the add-on row, for a bag added straight from a product page with no
     extra strap yet (core.extraStrapTarget): every strap the builder offers for
     that bag, in the builder's order, as tiles with the photo, the builder's
     translated name, the full price struck through and the 20% price. Nothing
     is chosen until the customer taps a tile; then a compact Add pill with the
     price appears (and, for rope + carabiners, a labelled rope picker and
     carabiner picker with product photos). Add gives the bag its strap in the
     cart (onAddExtra). Gated by the theme setting that switches the builder's
     offer on. */
  var EXTRA_KEY = 'extra-strap';

  function extraFor(mount, cart) {
    var x = mount.cfg.extra;
    if (!x) return null;
    var target = core.extraStrapTarget(cart.items || [], { root: rootPath() });
    if (!target) return null;
    var kinds = core.orderedExtraKinds(target.main_kind).filter(function (k) {
      return x.kinds[k] && x.kinds[k].available;
    });
    return kinds.length ? { target: target, kinds: kinds, items: cart.items || [] } : null;
  }

  function renderExtra(mount, extra) {
    var box = mount.extraEl;
    if (!extra) {
      if (box) box.hidden = true;
      mount.extraState = null;
      return;
    }
    var x = mount.cfg.extra;
    var ropeData = x.kinds.rope;
    var ropeAt = ropeData && isArr(ropeData.ropes) ? core.preferredPart(ropeData.ropes, extra.items) : 0;
    var clipAt = ropeData && isArr(ropeData.clips) ? core.preferredPart(ropeData.clips, extra.items) : 0;
    var sig = extra.target.bid + '|' + extra.kinds.join(',') + '|' + ropeAt + ',' + clipAt;
    if (box && box.getAttribute('data-sig') === sig && mount.extraState) {
      box.hidden = false;
      return;
    }
    if (!box) {
      box = el('section', 'cali-ku__extra');
      mount.root.insertBefore(box, mount.root.querySelector('.cali-ku__head'));
      mount.extraEl = box;
    }
    closeMenu(mount, false);
    while (box.firstChild) box.removeChild(box.firstChild);
    box.setAttribute('data-sig', sig);
    box.hidden = false;
    var idBase = 'cali-ku-x-' + mount.location + '-' + (++cardSeq);
    // Nothing is chosen until the customer taps a tile (owner, 10 Oct).
    var st = mount.extraState = { target: extra.target, kinds: extra.kinds, kind: null, tiles: {}, imgs: {}, names: {}, xcard: null };

    var head = el('div', 'cali-ku__extra-head');
    var heading = el('h3', 'cali-ku__heading', x.title);
    heading.id = idBase + '-h';
    head.appendChild(heading);
    // The builder's badge, its text included (a true minus sign).
    head.appendChild(el('span', 'cali-ku__pill-off', '\u221220%'));
    box.appendChild(head);
    box.setAttribute('aria-labelledby', heading.id);
    if (x.note) box.appendChild(el('p', 'cali-ku__extra-note', x.note));

    // One tile per strap: tap to choose it, like the builder's cards.
    var list = el('div', 'cali-ku__tiles');
    list.setAttribute('role', 'radiogroup');
    list.setAttribute('aria-labelledby', heading.id);
    extra.kinds.forEach(function (kind, i) {
      var data = x.kinds[kind];
      var tile = el('button', 'cali-ku__tile');
      tile.type = 'button';
      tile.setAttribute('role', 'radio');
      tile.setAttribute('data-kind', kind);
      var media = el('span', 'cali-ku__tile-media');
      var img = el('img', 'cali-ku__tile-img');
      img.alt = '';
      img.width = 64;
      img.height = 64;
      img.loading = 'lazy';
      img.decoding = 'async';
      // The rope tile shows the colour the pickers open on (owner, 10 Oct: not the black rope
      // when the customer already has one).
      var tileSrc = kind === 'rope' && isArr(data.ropes) && data.ropes[ropeAt] && data.ropes[ropeAt].img ? data.ropes[ropeAt].img : data.img;
      if (tileSrc) img.src = tileSrc;
      else img.hidden = true;
      media.appendChild(img);
      tile.appendChild(media);
      var name = el('span', 'cali-ku__tile-name', data.name || '');
      name.id = idBase + '-' + kind;
      tile.appendChild(name);
      var prices = el('span', 'cali-ku__tile-prices');
      var now = el('span', 'cali-ku__tile-now', data.offer_price || '');
      now.id = name.id + '-now';
      prices.appendChild(now);
      if (data.price && data.offer_cents < data.cents) prices.appendChild(el('s', 'cali-ku__tile-was', data.price));
      tile.appendChild(prices);
      tile.setAttribute('aria-labelledby', [name.id, now.id].join(' '));
      tile.addEventListener('click', function () { selectExtra(mount, kind); });
      tile.addEventListener('keydown', function (e) {
        var step = e.key === 'ArrowRight' || e.key === 'ArrowDown' ? 1 : e.key === 'ArrowLeft' || e.key === 'ArrowUp' ? -1 : 0;
        if (!step) return;
        e.preventDefault();
        var next = st.kinds[(i + step + st.kinds.length) % st.kinds.length];
        selectExtra(mount, next);
        try { st.tiles[next].focus(); } catch (err) {}
      });
      st.tiles[kind] = tile;
      st.imgs[kind] = img;
      st.names[kind] = name;
      list.appendChild(tile);
    });
    box.appendChild(list);

    // Rope + carabiners: a labelled rope picker and a labelled carabiner picker,
    // each showing the chosen colour's product photo and listing every colour
    // with its photo (owner, 10 Oct: two plain "Black" dropdowns said nothing).
    var pick = el('div', 'cali-ku__extra-pick');
    var rope = x.kinds.rope;
    if (rope && extra.kinds.indexOf('rope') !== -1 && isArr(rope.ropes) && rope.ropes.length && isArr(rope.clips) && rope.clips.length) {
      var make = function (label, values, poolKey) {
        return {
          title: label,
          options: [{ name: label, kind: 'colour', sw: swatchesOf(mount, poolKey) }],
          variants: values.map(function (v) { return { id: v.id, opts: [v.name], cents: v.cents }; }),
          thumbs: values.map(function (v) { return v.img || null; })
        };
      };
      var xcard = {
        products: [make(x.rope_label || '', rope.ropes, 'rope'), make(x.clip_label || '', rope.clips, 'carabiner')],
        selected: [ropeAt, clipAt],
        controls: []
      };
      xcard.onPick = function () {
        refreshControls(xcard);
        // The rope tile shows the rope colour chosen.
        var src = xcard.products[0].thumbs[xcard.selected[0]] || rope.img;
        if (src && st.imgs.rope && st.imgs.rope.getAttribute('src') !== src) st.imgs.rope.src = src;
        updateExtraPrice(mount);
      };
      xcard.products.forEach(function (product, partIdx) {
        var field = el('div', 'cali-ku__field');
        var label = el('span', 'cali-ku__field-label', product.title);
        label.setAttribute('aria-hidden', 'true');
        field.appendChild(label);
        // A part with a single colour on sale still shows what is included.
        if (product.variants.length < 2) {
          var only = el('span', 'cali-ku__opt cali-ku__opt--thumb cali-ku__opt--fixed');
          field.appendChild(only);
          if (product.thumbs[0]) {
            var oi = el('img', 'cali-ku__opt-thumb');
            oi.alt = '';
            oi.src = product.thumbs[0];
            only.appendChild(oi);
          }
          only.appendChild(el('span', 'cali-ku__opt-text', product.variants[0].opts[0] || ''));
        } else {
          buildPartControls(mount, xcard, product, partIdx, field, false);
        }
        pick.appendChild(field);
      });
      refreshControls(xcard);
      st.xcard = xcard;
    }
    box.appendChild(pick);
    st.pick = pick;

    var error = el('div', 'cali-ku__error');
    error.setAttribute('role', 'status');
    error.hidden = true;
    box.appendChild(error);
    st.error = error;

    var add = el('button', 'cali-ku__add cali-ku__extra-add');
    add.type = 'button';
    var addLabel = el('span', 'cali-ku__add-label', mount.cfg.copy.add);
    addLabel.id = idBase + '-add';
    add.appendChild(addLabel);
    var price = el('span', 'cali-ku__add-price');
    price.id = idBase + '-price';
    add.appendChild(price);
    add.addEventListener('click', function () { onAddExtra(mount); });
    box.appendChild(add);
    st.add = add;
    st.addLabel = addLabel;
    st.price = price;

    selectExtra(mount, null);
    observeExtra(mount, box, extra);
  }

  function isArr(v) {
    return Object.prototype.toString.call(v) === '[object Array]';
  }

  /* Colour circles for a pool product's colour option, when the pool has it. */
  function swatchesOf(mount, poolKey) {
    var p = mount.cfg.products && mount.cfg.products[poolKey];
    var opts = (p && p.options) || [];
    for (var i = 0; i < opts.length; i++) {
      if (opts[i] && opts[i].sw && Object.keys(opts[i].sw).length) return opts[i].sw;
    }
    return {};
  }

  /* `kind` null: nothing chosen, so no pickers and no Add. With no tile
     chosen, the first one takes the Tab stop (a radio group's rule). */
  function selectExtra(mount, kind) {
    var st = mount.extraState;
    if (!st) return;
    st.kind = kind || null;
    st.kinds.forEach(function (k, i) {
      var on = k === st.kind;
      st.tiles[k].classList.toggle('is-selected', on);
      st.tiles[k].setAttribute('aria-checked', on ? 'true' : 'false');
      st.tiles[k].tabIndex = on || (!st.kind && i === 0) ? 0 : -1;
    });
    st.pick.hidden = !(st.kind === 'rope' && st.xcard);
    // Fetch every colour's photo once rope is chosen, so a list opens with its photos.
    if (st.kind === 'rope' && st.xcard && !st.preloaded) {
      st.preloaded = true;
      st.xcard.products.forEach(function (p) {
        p.thumbs.forEach(function (src) {
          if (src) (new Image()).src = src;
        });
      });
    }
    closeMenu(mount, false);
    st.error.hidden = true;
    st.error.textContent = '';
    st.add.hidden = !st.kind;
    if (st.kind) st.add.setAttribute('aria-labelledby', [st.addLabel.id, st.names[st.kind].id, st.price.id].join(' '));
    updateExtraPrice(mount);
  }

  /* The chosen strap as { kind, components: [{ role, id }], cents (standalone total) }. */
  function extraSelection(mount) {
    var st = mount.extraState;
    var data = st && st.kind ? mount.cfg.extra.kinds[st.kind] : null;
    if (!data) return null;
    if (st.kind === 'rope') {
      var r = st.xcard ? st.xcard.products[0].variants[st.xcard.selected[0]] : data.ropes && data.ropes[0];
      var c = st.xcard ? st.xcard.products[1].variants[st.xcard.selected[1]] : data.clips && data.clips[0];
      if (!r || !c) return null;
      return { kind: 'rope', components: [{ role: 'rope', id: r.id }, { role: 'carabiner', id: c.id }], cents: (Number(r.cents) || 0) + (Number(c.cents) || 0) };
    }
    return { kind: st.kind, components: [{ role: 'strap', id: data.id }], cents: Number(data.cents) || 0 };
  }

  function updateExtraPrice(mount) {
    var st = mount.extraState;
    var data = st.kind ? mount.cfg.extra.kinds[st.kind] : null;
    if (!data) {
      st.offerCents = 0;
      st.price.textContent = '';
      return;
    }
    var sel = extraSelection(mount);
    var cents = sel ? core.extraStrapCents(sel.cents) : 0;
    st.offerCents = cents;
    st.price.textContent = sel && cents === data.offer_cents ? data.offer_price : mount.format ? mount.format(cents) : data.offer_price;
  }

  function freshBid() {
    try {
      if (window.crypto && typeof window.crypto.randomUUID === 'function') return window.crypto.randomUUID();
    } catch (err) {}
    return 'cali-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
  }

  function readCart() {
    return window.fetch(rootPath() + 'cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(function (res) {
        if (!res.ok) throw new Error('cart read failed');
        return res.json();
      });
  }

  function postCart(path, body) {
    return window.fetch(rootPath() + path, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (res) {
      return res.json().then(function (json) {
        if (!res.ok) {
          var e = new Error(path + ' failed');
          e.userMessage = stripTags((json && (json.description || json.message)) || '');
          throw e;
        }
        return json;
      }, function () {
        if (!res.ok) throw new Error(path + ' failed');
        return null;
      });
    });
  }

  function linesOf(cart, bid) {
    return ((cart && cart.items) || []).filter(function (it) {
      return it && it.properties && typeof it.properties === 'object' && it.properties._bid === bid;
    });
  }

  /* Gives the bag its extra strap in the cart, the way the builder's "Update my
     bag" does it: the whole group is added again with the strap under a new bundle
     id, checked line by line in a fresh cart, and only then is the old group
     removed. A failed or partial add leaves the old bag as it was (any new line is
     cleaned up); if the old group cannot be removed, the checked new bag is kept
     and the customer is told to check the cart. Runs in the shared cart-write
     queue, so it never overlaps another write. */
  function onAddExtra(mount) {
    var st = mount.extraState;
    if (!st || mount.adding || mount.awaitingRerank) return;
    var sel = extraSelection(mount);
    if (!sel) return;
    var bid = st.target.bid;
    var fresh = freshBid();
    var value = (st.offerCents || 0) / 100;
    mount.adding = true;
    st.add.disabled = true;
    st.add.setAttribute('aria-busy', 'true');
    st.error.hidden = true;

    var write = function () {
      return readCart().then(function (cart) {
        var plan = core.extraStrapItems(cart.items, bid, sel, fresh);
        if (!plan) throw new Error('bag changed');
        return postCart('cart/add.js', { items: plan.items }).then(function () {
          return readCart();
        }).then(function (after) {
          if (linesOf(after, fresh).length !== plan.items.length) throw new Error('new bag incomplete');
          var updates = {};
          plan.removeKeys.forEach(function (k) { updates[k] = 0; });
          return postCart('cart/update.js', { updates: updates }).then(function () {
            return readCart();
          }).then(function (done) {
            if (linesOf(done, bid).length) {
              var e = new Error('old bag still there');
              e.keepNew = true;
              throw e;
            }
            return plan;
          }, function (err) {
            err.keepNew = true;
            throw err;
          });
        }).catch(function (err) {
          if (err && err.keepNew) throw err;
          // Nothing of the new bag may stay half-added; the old bag was not touched.
          return readCart().then(function (cart2) {
            var updates = {};
            linesOf(cart2, fresh).forEach(function (it) { updates[it.key] = 0; });
            if (!Object.keys(updates).length) return null;
            return postCart('cart/update.js', { updates: updates });
          }).catch(function () { return null; }).then(function () { throw err; });
        });
      });
    };
    var runner = window.caliCartSync && window.caliCartSync.runMutation;

    (runner ? runner(write) : write())
      .then(function () {
        track('accept', {
          upsell_offer: EXTRA_KEY,
          upsell_extra_kind: sel.kind,
          upsell_extra_count: ((st.target.extras && st.target.extras.length) || 0) + 1,
          upsell_segment: mount.segmentKey,
          upsell_position: 0,
          upsell_arm: mount.arm,
          upsell_value: value,
          upsell_location: mount.location
        });
        mount.adding = false;
        mount.awaitingRerank = true;
        setTimeout(function () {
          if (!mount.awaitingRerank) return;
          mount.awaitingRerank = false;
          if (mount.extraState && mount.extraState.add) {
            mount.extraState.add.disabled = false;
            mount.extraState.add.removeAttribute('aria-busy');
          }
        }, RERANK_TIMEOUT_MS);
        mount.pendingCart = null;
        if (!runner) {
          try { document.dispatchEvent(new CustomEvent('cart:build')); } catch (err) {}
        }
        refreshNow();
        revealAddedExtra(mount, fresh);
      })
      .catch(function (err) {
        mount.adding = false;
        if (mount.extraState === st) {
          st.add.disabled = false;
          st.add.removeAttribute('aria-busy');
          st.error.textContent = (err && err.userMessage) || mount.cfg.strings.error || '';
          st.error.hidden = !st.error.textContent;
        }
        if (!runner) {
          try { document.dispatchEvent(new CustomEvent('cart:build')); } catch (e2) {}
        }
        refreshNow();
      });
  }

  /* One impression per bag offer, segment and surface per session, at 50 % visible. */
  function observeExtra(mount, box, extra) {
    if (!('IntersectionObserver' in window)) return;
    if (mount.extraObserver) {
      try { mount.extraObserver.disconnect(); } catch (err) {}
    }
    mount.extraObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting || mount.root.hidden || box.hidden) return;
        mount.extraObserver.unobserve(entry.target);
        var seenKey = [EXTRA_KEY, extra.kinds.join(','), mount.segmentKey || '', mount.location].join('|');
        if (ssList(SS.seen).indexOf(seenKey) !== -1) return;
        ssPush(SS.seen, seenKey);
        var lead = mount.cfg.extra.kinds[extra.kinds[0]];
        track('impression', {
          upsell_offer: EXTRA_KEY,
          upsell_extra_kinds: extra.kinds.join(','),
          upsell_segment: mount.segmentKey,
          upsell_position: 0,
          upsell_arm: mount.arm,
          upsell_value: (Number(lead && lead.offer_cents) || 0) / 100,
          upsell_location: mount.location
        });
      });
    }, { threshold: 0.5 });
    mount.extraObserver.observe(box);
  }

  /* ---- an add-on card (cali 2026-10-10, owner's UI pass) ----
     Two cards side by side: photo, name, price and rating, one dropdown per
     choice (colour circles with their names), Add. */
  function buildCardNode(mount, card) {
    var li = el('li', 'cali-ku__card');
    li.setAttribute('data-key', card.key);
    var lead = card.products[0];
    var multi = card.parts.length > 1;
    var title = card.products.map(function (p) { return p.title; }).join(' + ');
    var idBase = 'cali-ku-' + mount.location + '-' + (++cardSeq);

    // cali 2026-10-07 kit upsell: the photo and the name open the product
    // preview over the cart (owner, 7 Oct), never a page change.
    var media = el('button', 'cali-ku__media');
    media.type = 'button';
    media.setAttribute('tabindex', '-1');
    media.setAttribute('aria-hidden', 'true');
    media.addEventListener('click', function () { openPreview(mount, card); });
    var img = el('img', 'cali-ku__img');
    img.alt = '';
    img.width = 64;
    img.height = 64;
    img.loading = 'lazy';
    img.decoding = 'async';
    media.appendChild(img);
    li.appendChild(media);

    var body = el('div', 'cali-ku__body');
    var link = el('button', 'cali-ku__title');
    link.type = 'button';
    // A button cannot clamp its own lines (Chrome lays it out as flow-root).
    link.appendChild(el('span', 'cali-ku__title-text', title));
    link.setAttribute('aria-haspopup', 'dialog');
    link.addEventListener('click', function () { openPreview(mount, card); });
    link.id = idBase + '-title';
    body.appendChild(link);

    var meta = el('div', 'cali-ku__meta');
    var price = el('span', 'cali-ku__price');
    price.id = idBase + '-price';
    meta.appendChild(price);
    var rating = buildRating(mount, lead, multi);
    if (rating) meta.appendChild(rating);
    body.appendChild(meta);

    var picker = el('div', 'cali-ku__picker');
    card.controls = [];
    card.products.forEach(function (product, partIdx) {
      buildPartControls(mount, card, product, partIdx, picker, multi);
    });
    body.appendChild(picker);
    var error = el('div', 'cali-ku__error');
    error.setAttribute('role', 'status');
    error.hidden = true;
    body.appendChild(error);

    var add = el('button', 'cali-ku__add');
    add.type = 'button';
    var addLabel = el('span', 'cali-ku__add-label', mount.cfg.copy.add);
    addLabel.id = idBase + '-add';
    add.appendChild(addLabel);
    // cali 2026-10-07 kit upsell: read as "Add, <product>, <price>", built from
    // the visible nodes so no translated sentence has to be assembled here.
    add.setAttribute('aria-labelledby', [addLabel.id, link.id, price.id].join(' '));
    add.addEventListener('click', function () { onAdd(mount, card); });
    body.appendChild(add);
    li.appendChild(body);

    card.els = { img: img, price: price, add: add, error: error, meta: meta, picker: picker, title: link };
    refreshCard(mount, card);
    return li;
  }

  /* One dropdown per product with more than one variant on sale: the chosen
     variant's colour circle (when its colour is known) and its name, opening a
     list of every variant with its circle and name (owner, 10 Oct). A product
     with colour and size gets ONE list ("Mustard · Small"), so every card keeps
     one row of choices and the same height; a two-part card has two side by
     side. */
  function buildPartControls(mount, card, product, partIdx, picker, multi) {
    var variants = product.variants || [];
    if (variants.length < 2) return;
    var options = product.options || [];
    // Only the options that differ between the variants on sale name a variant.
    var varying = options.map(function (opt, oi) {
      var first = (variants[0].opts || [])[oi];
      return variants.some(function (v) { return (v.opts || [])[oi] !== first; });
    });
    // A circle wherever the colour is known (a value without one shows its name only).
    var colourAt = -1;
    options.forEach(function (opt, oi) {
      if (colourAt === -1 && opt.sw && Object.keys(opt.sw).length && varying[oi]) colourAt = oi;
    });
    var sw = colourAt === -1 ? {} : options[colourAt].sw;
    var names = variants.map(function (v) {
      var parts = (v.opts || []).filter(function (val, oi) { return varying[oi] && val != null && val !== ''; });
      return parts.length ? parts.join(' · ') : (v.title || '');
    });
    // A product photo per variant (the strap block's rope and carabiners) takes
    // the circle's place, in the button and in the list.
    var thumbs = isArr(product.thumbs) && product.thumbs.some(Boolean) ? product.thumbs : null;
    var btn = el('button', thumbs ? 'cali-ku__opt cali-ku__opt--thumb' : 'cali-ku__opt');
    btn.type = 'button';
    btn.setAttribute('aria-haspopup', 'listbox');
    btn.setAttribute('aria-expanded', 'false');
    var swatch = el('span', 'cali-ku__swatch');
    swatch.setAttribute('aria-hidden', 'true');
    var thumb = null;
    if (thumbs) {
      thumb = el('img', 'cali-ku__opt-thumb');
      thumb.alt = '';
      thumb.decoding = 'async';
      btn.appendChild(thumb);
    }
    var text = el('span', 'cali-ku__opt-text');
    btn.appendChild(swatch);
    btn.appendChild(text);
    btn.appendChild(chevronIcon());
    var optNames = options.filter(function (opt, oi) { return varying[oi]; }).map(function (opt) { return opt.name; });
    var ctl = {
      btn: btn,
      swatch: swatch,
      thumb: thumb,
      thumbs: thumbs,
      text: text,
      part: partIdx,
      colourAt: colourAt,
      sw: sw,
      names: names,
      label: (multi ? product.title + ': ' : '') + optNames.join(' / ')
    };
    btn.addEventListener('click', function () { toggleMenu(mount, card, ctl); });
    btn.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        openMenu(mount, card, ctl);
      }
    });
    card.controls.push(ctl);
    picker.appendChild(btn);
  }

  /* The colour of variant `idx` of a control's product, or null. */
  function ctlColour(card, ctl, idx) {
    if (ctl.colourAt === -1) return null;
    var v = card.products[ctl.part].variants[idx];
    var val = v && (v.opts || [])[ctl.colourAt];
    return val != null && ctl.sw[val] ? ctl.sw[val] : null;
  }

  function chevronIcon() {
    var svg = document.createElementNS(SVG_NS, 'svg');
    svg.setAttribute('viewBox', '0 0 12 12');
    svg.setAttribute('width', '10');
    svg.setAttribute('height', '10');
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('focusable', 'false');
    svg.setAttribute('class', 'cali-ku__chev');
    var path = document.createElementNS(SVG_NS, 'path');
    path.setAttribute('d', 'M2.5 4.5 6 8l3.5-3.5');
    path.setAttribute('fill', 'none');
    path.setAttribute('stroke', 'currentColor');
    path.setAttribute('stroke-width', '1.5');
    svg.appendChild(path);
    return svg;
  }

  /* ---- the dropdown list ----
     One list per carousel, inside it (so inside Motion's .drawer: a click in it
     never reads as "outside the cart"). It is hidden, never removed, while a
     click is still on its way to the window, for the same reason. Placed under
     its button with absolute positioning, because the row scrolls sideways
     and would clip anything inside it. */
  var openMenus = [];

  function closeMenu(mount, refocus) {
    var m = mount.menu;
    if (!m || m.el.hidden) return;
    m.el.hidden = true;
    var btn = m.ctl && m.ctl.btn;
    m.ctl = null;
    var at = openMenus.indexOf(mount);
    if (at !== -1) openMenus.splice(at, 1);
    if (btn) {
      btn.setAttribute('aria-expanded', 'false');
      if (refocus) {
        try { btn.focus({ preventScroll: true }); } catch (err) {}
      }
    }
  }

  function toggleMenu(mount, card, ctl) {
    if (mount.menu && !mount.menu.el.hidden && mount.menu.ctl === ctl) {
      closeMenu(mount, true);
      return;
    }
    openMenu(mount, card, ctl);
  }

  function openMenu(mount, card, ctl) {
    closeMenu(mount, false);
    if (!mount.menu) {
      var list = el('ul', 'cali-ku__menu');
      list.setAttribute('role', 'listbox');
      list.hidden = true;
      list.addEventListener('keydown', function (e) { menuKey(mount, e); });
      mount.root.appendChild(list);
      mount.menu = { el: list, ctl: null };
    }
    var ul = mount.menu.el;
    while (ul.firstChild) ul.removeChild(ul.firstChild);
    ul.setAttribute('aria-label', ctl.label);
    ul.classList.toggle('cali-ku__menu--thumbs', !!ctl.thumbs);
    var current = card.selected[ctl.part];
    var selected = null;
    ctl.names.forEach(function (name, idx) {
      var item = el('li', 'cali-ku__menu-item');
      item.setAttribute('role', 'option');
      item.tabIndex = -1;
      item.setAttribute('aria-selected', idx === current ? 'true' : 'false');
      if (ctl.thumbs) {
        var pic = el('img', 'cali-ku__menu-thumb');
        pic.alt = '';
        pic.decoding = 'async';
        if (ctl.thumbs[idx]) pic.src = ctl.thumbs[idx];
        else pic.style.visibility = 'hidden';
        item.appendChild(pic);
      } else {
        var dot = el('span', 'cali-ku__swatch');
        dot.setAttribute('aria-hidden', 'true');
        var colour = ctlColour(card, ctl, idx);
        if (colour) dot.style.backgroundColor = colour;
        else dot.hidden = true;
        item.appendChild(dot);
      }
      item.appendChild(el('span', 'cali-ku__menu-text', name));
      item.addEventListener('click', function () {
        closeMenu(mount, true);
        card.selected[ctl.part] = idx;
        if (card.onPick) {
          card.onPick();
          return;
        }
        clearError(card);
        refreshCard(mount, card);
      });
      ul.appendChild(item);
      if (idx === current) selected = item;
    });
    mount.menu.ctl = ctl;
    openMenus.push(mount);
    ul.hidden = false;
    var rr = mount.root.getBoundingClientRect();
    var br = ctl.btn.getBoundingClientRect();
    var w = Math.min(Math.max(br.width, ctl.thumbs ? 200 : 176), rr.width);
    var left = Math.min(Math.max(0, br.left - rr.left), Math.max(0, rr.width - w));
    ul.style.width = w + 'px';
    ul.style.left = left + 'px';
    ul.style.top = (br.bottom - rr.top + 4) + 'px';
    // The cart drawer scrolls: a list that would run past the bottom of what is
    // visible scrolls the drawer just enough to show it whole. (Opening it
    // upwards put its first lines under the drawer's free-delivery bar.)
    revealBelow(ul, mount.root.closest('.drawer__scrollable'));
    ctl.btn.setAttribute('aria-expanded', 'true');
    var focusItem = selected || ul.firstChild;
    if (focusItem) {
      try { focusItem.focus({ preventScroll: true }); } catch (err) {}
      if (focusItem.scrollIntoView && ul.scrollHeight > ul.clientHeight) {
        ul.scrollTop = Math.max(0, focusItem.offsetTop - ul.clientHeight / 2);
      }
    }
  }

  /* cali 2026-10-10 (owner, phone test): a list that opens past the bottom of
     what is visible brings itself into view with a short eased scroll (240 ms,
     ease-out), so the eye follows the move, instead of a jump. The drawer's own
     scroll area when there is one, else the page; 8px of air below the list.
     Reduced motion: the jump. */
  var REVEAL_MS = 240;

  /* The eased move itself: `scroller` (or the page) from where it is to `to`,
     clamped to what can scroll. */
  function easeScroll(scroller, to) {
    var doc = document.documentElement;
    var from = scrollPosition(scroller);
    var max = scroller ? scroller.scrollHeight - scroller.clientHeight : doc.scrollHeight - window.innerHeight;
    to = Math.max(0, Math.min(max, to));
    var set = function (y) {
      if (scroller) scroller.scrollTop = y;
      else window.scrollTo(0, y);
    };
    if (Math.abs(to - from) < 1) return;
    if (prefersReducedMotion() || !window.requestAnimationFrame) {
      set(to);
      return;
    }
    var start = null;
    var step = function (now) {
      if (start === null) start = now;
      var t = Math.min(1, (now - start) / REVEAL_MS);
      set(from + (to - from) * (1 - Math.pow(1 - t, 3)));
      if (t < 1) window.requestAnimationFrame(step);
    };
    window.requestAnimationFrame(step);
  }

  function scrollPosition(scroller) {
    return scroller ? scroller.scrollTop : (window.pageYOffset || document.documentElement.scrollTop || 0);
  }

  function revealBelow(el, scroller) {
    var edge = scroller ? scroller.getBoundingClientRect().bottom : window.innerHeight;
    var by = el.getBoundingClientRect().bottom - edge + 8;
    if (by <= 0) return;
    easeScroll(scroller, scrollPosition(scroller) + by);
  }

  /* cali 2026-10-10 (owner): after an extra strap is added in the cart, the cart
     goes back (eased) to the strap just added, under its bag, where it can be
     seen and removed. The cart lines are drawn again after the write, so wait
     (up to 4 s) for the new bag's last extra block; nothing moves when it is
     already in view. */
  function revealAddedExtra(mount, bid) {
    var scroller = mount.root.closest('.drawer__scrollable');
    var scope = scroller || document;
    var waited = 0;
    var tick = function () {
      var blocks = scope.querySelectorAll('.cali-extra-cart[data-cali-extra-bid]');
      var last = null;
      for (var i = 0; i < blocks.length; i++) {
        if (blocks[i].getAttribute('data-cali-extra-bid') === bid) last = blocks[i];
      }
      if (!last || !last.getClientRects().length) {
        waited += 100;
        if (waited < 4000) setTimeout(tick, 100);
        return;
      }
      var r = last.getBoundingClientRect();
      var top = scroller ? scroller.getBoundingClientRect().top : 0;
      var bottom = scroller ? scroller.getBoundingClientRect().bottom : window.innerHeight;
      if (r.top < top + 8) easeScroll(scroller, scrollPosition(scroller) - (top + 12 - r.top));
      else if (r.bottom > bottom - 8) easeScroll(scroller, scrollPosition(scroller) + Math.min(r.bottom - bottom + 12, r.top - top - 12));
    };
    setTimeout(tick, 100);
  }

  function menuKey(mount, e) {
    var items = Array.prototype.slice.call(mount.menu.el.children);
    var i = items.indexOf(document.activeElement);
    var go = function (j) {
      var it = items[Math.max(0, Math.min(items.length - 1, j))];
      if (it) it.focus();
    };
    if (e.key === 'ArrowDown') { e.preventDefault(); go(i + 1); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); go(i - 1); }
    else if (e.key === 'Home') { e.preventDefault(); go(0); }
    else if (e.key === 'End') { e.preventDefault(); go(items.length - 1); }
    else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (items[i]) items[i].click();
    } else if (e.key === 'Escape' || e.keyCode === 27) {
      // Motion closes the cart on the Escape keyup: swallow that one.
      e.preventDefault();
      e.stopPropagation();
      swallowEscapeUp = true;
      setTimeout(function () { swallowEscapeUp = false; }, 1000);
      closeMenu(mount, true);
    } else if (e.key === 'Tab') {
      closeMenu(mount, false);
    }
  }

  // A press anywhere else closes an open list; so does scrolling the page.
  document.addEventListener('pointerdown', function (e) {
    openMenus.slice().forEach(function (m) {
      var ctl = m.menu && m.menu.ctl;
      if (!ctl) return;
      if (m.menu.el.contains(e.target) || ctl.btn.contains(e.target)) return;
      closeMenu(m, false);
    });
  }, true);
  window.addEventListener('resize', function () {
    openMenus.slice().forEach(function (m) { closeMenu(m, false); });
  });

  function refreshCard(mount, card) {
    var vs = cardVariants(card);
    card.els.price.textContent = cardPrice(mount, card);
    var src = vs[0].img || null;
    if (src) {
      if (card.els.img.getAttribute('src') !== src) card.els.img.src = src;
      card.els.img.hidden = false;
    } else {
      card.els.img.hidden = true;
    }
    refreshControls(card);
  }

  /* Each dropdown shows its product's chosen variant: circle, name, label. */
  function refreshControls(card) {
    (card.controls || []).forEach(function (ctl) {
      var idx = card.selected[ctl.part];
      var name = ctl.names[idx] || '';
      ctl.text.textContent = name;
      if (ctl.thumb) {
        var src = ctl.thumbs[idx];
        if (src && ctl.thumb.getAttribute('src') !== src) ctl.thumb.src = src;
        ctl.thumb.style.visibility = src ? '' : 'hidden';
        ctl.swatch.hidden = true;
        ctl.btn.setAttribute('aria-label', ctl.label + ': ' + name);
        return;
      }
      var colour = ctlColour(card, ctl, idx);
      if (colour) {
        ctl.swatch.style.backgroundColor = colour;
        ctl.swatch.hidden = false;
      } else {
        ctl.swatch.hidden = true;
      }
      ctl.btn.setAttribute('aria-label', ctl.label + ': ' + name);
    });
  }

  function showError(mount, card, message) {
    clearError(card);
    card.els.error.textContent = message || mount.cfg.strings.error || '';
    if (!card.els.error.textContent) return;
    card.els.error.hidden = false;
    card.node.classList.add('has-error');
    card.errorTimer = setTimeout(function () { clearError(card); }, ERROR_MS);
  }

  function clearError(card) {
    if (card.errorTimer) {
      clearTimeout(card.errorTimer);
      card.errorTimer = null;
    }
    if (!card.els) return;
    card.els.error.hidden = true;
    card.els.error.textContent = '';
    if (card.node) card.node.classList.remove('has-error');
  }

  /* ---- actions ---- */
  function onAdd(mount, card) {
    // awaitingRerank: the add succeeded but the card is still on screen until
    // the fresh cart re-ranks it away; a second tap then would add it twice.
    if (mount.adding || mount.awaitingRerank) return;
    var vs = cardVariants(card);
    var items = core.buildAddItems(vs.map(function (v) { return v.id; }), card.key, mount.segmentKey || '', mount.arm || '');
    var value = cardCents(card) / 100;
    mount.adding = true;
    card.els.add.disabled = true;
    card.els.add.setAttribute('aria-busy', 'true');
    clearError(card);

    var write = function () {
      return window.fetch(rootPath() + 'cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: items })
      }).then(function (res) {
        return res.json().then(function (json) {
          if (!res.ok) {
            var e = new Error('add failed');
            e.userMessage = stripTags((json && (json.description || json.message)) || '');
            throw e;
          }
          return json;
        }, function () {
          if (!res.ok) throw new Error('add failed');
          return null;
        });
      });
    };
    // cali 2026-10-07 kit upsell: through the shared cart-write queue
    // (assets/cali-cart-sync.js), like shipping protection and the gift offer:
    // writes never overlap, and Checkout is held until Motion's rows match
    // /cart.js again (its form posts positional updates[]). The queue
    // dispatches cart:build itself once the write settles.
    var runner = window.caliCartSync && window.caliCartSync.runMutation;

    (runner ? runner(write) : write())
      .then(function () {
        track('accept', {
          upsell_offer: card.key,
          upsell_segment: mount.segmentKey,
          upsell_position: card.position,
          upsell_arm: mount.arm,
          upsell_value: value,
          upsell_variants: items.map(function (i) { return i.id; }).join(','),
          upsell_location: mount.location
        });
        mount.adding = false;
        mount.awaitingRerank = true;
        mount.scrollToStart = true;
        // The button stays disabled until the re-rank (render re-enables a card
        // that stays). Safety net if no fresh cart ever arrives.
        setTimeout(function () {
          if (!mount.awaitingRerank) return;
          mount.awaitingRerank = false;
          if (card.els) {
            card.els.add.disabled = false;
            card.els.add.removeAttribute('aria-busy');
          }
        }, RERANK_TIMEOUT_MS);
        mount.pendingCart = null;
        // Motion rebuilds the line items on cart:build (the queue sends it when
        // it ran the write); the re-rank follows from the fresh /cart.js.
        if (!runner) {
          try { document.dispatchEvent(new CustomEvent('cart:build')); } catch (err) {}
        }
        refreshNow();
      })
      .catch(function (err) {
        mount.adding = false;
        if (card.els) {
          card.els.add.disabled = false;
          card.els.add.removeAttribute('aria-busy');
          showError(mount, card, err && err.userMessage);
        }
        flushPending(mount);
      });
  }

  /* ---- product preview ----
     cali 2026-10-07 kit upsell (owner, 7 Oct): the name or the photo opens a
     preview of the product over the cart: its photos, name, rating, price and
     description, and a link to the product page. Nothing here leaves the cart.
     Motion closes the drawer on any window click whose target is not inside
     `.drawer` (and on Escape), so the preview lives inside the drawer and stops
     its own clicks and Escape from reaching the window: tapping outside the
     sheet closes the preview only. Photos and description come from the
     storefront's own product JSON (`<locale root>products/<handle>.js`, in the
     visitor's language), fetched once per product on first open. */
  var productCache = {};
  var openPop = null;
  // Escape closes the preview on keydown and focus goes back to the card, so
  // the keyup lands outside the preview, where Motion would read it as "close
  // the drawer". That one keyup is swallowed before Motion's window listener.
  var swallowEscapeUp = false;
  window.addEventListener('keyup', function (evt) {
    if (!swallowEscapeUp || !(evt.key === 'Escape' || evt.keyCode === 27)) return;
    swallowEscapeUp = false;
    evt.stopPropagation();
  }, true);

  function fetchProduct(handle) {
    if (!productCache[handle]) {
      productCache[handle] = window.fetch(rootPath() + 'products/' + encodeURIComponent(handle) + '.js', {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' }
      }).then(function (res) {
        if (!res.ok) throw new Error('product ' + res.status);
        return res.json();
      });
      productCache[handle].catch(function () { delete productCache[handle]; });
    }
    return productCache[handle];
  }

  /* Merchant HTML from the product description, reduced to text structure:
     paragraphs, line breaks, lists and emphasis survive, every attribute and
     every other element (scripts, iframes, images, styles) does not. */
  var DESC_KEEP = { P: 'p', BR: 'br', UL: 'ul', OL: 'ol', LI: 'li', STRONG: 'strong', B: 'strong', EM: 'em', I: 'em', H1: 'p', H2: 'p', H3: 'p', H4: 'p', H5: 'p', H6: 'p' };
  var DESC_DROP = { SCRIPT: 1, STYLE: 1, IFRAME: 1, OBJECT: 1, EMBED: 1, IMG: 1, VIDEO: 1, SVG: 1, TEMPLATE: 1, NOSCRIPT: 1, FORM: 1, INPUT: 1, BUTTON: 1 };

  function cleanDescription(html) {
    var out = document.createDocumentFragment();
    var doc;
    try {
      doc = new DOMParser().parseFromString('<div>' + String(html || '') + '</div>', 'text/html');
    } catch (err) {
      return out;
    }
    (function copy(from, to) {
      for (var n = from.firstChild; n; n = n.nextSibling) {
        if (n.nodeType === 3) {
          to.appendChild(document.createTextNode(n.nodeValue));
        } else if (n.nodeType === 1) {
          var tag = n.nodeName.toUpperCase();
          if (DESC_DROP[tag]) continue;
          if (DESC_KEEP[tag]) {
            var keep = document.createElement(DESC_KEEP[tag]);
            if (/^H[1-6]$/.test(tag)) keep.className = 'cali-ku-pop__subhead';
            copy(n, keep);
            to.appendChild(keep);
          } else {
            copy(n, to); // unknown wrapper (div, span, a, table…): keep its text
          }
        }
      }
    })(doc.body.firstChild || doc.body, out);
    return out;
  }

  function sizedImage(src, width) {
    if (!src) return null;
    var u = String(src);
    if (u.slice(0, 2) === '//') u = 'https:' + u;
    u = u.replace(/([?&])width=\d+&?/, '$1').replace(/[?&]$/, '');
    return u + (u.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
  }

  function popFocusables(pop) {
    return Array.prototype.filter.call(
      pop.querySelectorAll('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'),
      function (n) { return n.offsetParent !== null; }
    );
  }

  function closePreview() {
    if (!openPop) return;
    var p = openPop;
    openPop = null;
    p.node.classList.remove('is-open');
    var remove = function () { if (p.node.parentNode) p.node.parentNode.removeChild(p.node); };
    if (prefersReducedMotion()) remove();
    else setTimeout(remove, 220);
    if (p.returnTo && document.body.contains(p.returnTo)) {
      try { p.returnTo.focus({ preventScroll: true }); } catch (err) {}
    }
  }

  function openPreview(mount, card) {
    closeMenu(mount, false);
    closePreview();
    var strings = mount.cfg.strings;
    var lead = card.products[0];
    var variant = cardVariants(card)[0];
    var title = card.products.map(function (p) { return p.title; }).join(' + ');
    var href = lead.url + (variant && variant.id && lead.variants.length > 1 ? (lead.url.indexOf('?') === -1 ? '?' : '&') + 'variant=' + variant.id : '');
    var idBase = 'cali-ku-pop-' + (++cardSeq);

    var pop = el('div', 'cali-ku-pop');
    pop.setAttribute('role', 'dialog');
    pop.setAttribute('aria-modal', 'true');
    pop.setAttribute('aria-labelledby', idBase + '-title');
    var backdrop = el('div', 'cali-ku-pop__backdrop');
    pop.appendChild(backdrop);
    var sheet = el('div', 'cali-ku-pop__sheet');
    pop.appendChild(sheet);

    var close = el('button', 'cali-ku-pop__close');
    close.type = 'button';
    if (strings.close) close.setAttribute('aria-label', strings.close);
    close.innerHTML = '<svg aria-hidden="true" focusable="false" viewBox="0 0 64 64" width="16" height="16"><path d="m19 17.61 27.12 27.13m0-27.12L19 44.74" stroke="currentColor" stroke-width="5" fill="none"/></svg>';
    sheet.appendChild(close);

    var scroller = el('div', 'cali-ku-pop__scroll');
    sheet.appendChild(scroller);

    var gallery = el('div', 'cali-ku-pop__gallery');
    var slides = el('ul', 'cali-ku-pop__slides');
    slides.setAttribute('role', 'list');
    gallery.appendChild(slides);
    var prev = el('button', 'cali-ku-pop__arrow cali-ku-pop__arrow--prev');
    prev.type = 'button';
    if (strings.prev) prev.setAttribute('aria-label', strings.prev);
    prev.innerHTML = '<svg aria-hidden="true" focusable="false" viewBox="0 0 16 16" width="14" height="14"><path d="M10 3 5 8l5 5" fill="none" stroke="currentColor" stroke-width="1.6"/></svg>';
    var next = el('button', 'cali-ku-pop__arrow cali-ku-pop__arrow--next');
    next.type = 'button';
    if (strings.next) next.setAttribute('aria-label', strings.next);
    next.innerHTML = '<svg aria-hidden="true" focusable="false" viewBox="0 0 16 16" width="14" height="14"><path d="m6 3 5 5-5 5" fill="none" stroke="currentColor" stroke-width="1.6"/></svg>';
    var counter = el('span', 'cali-ku-pop__count');
    counter.setAttribute('aria-hidden', 'true');
    gallery.appendChild(prev);
    gallery.appendChild(next);
    gallery.appendChild(counter);
    scroller.appendChild(gallery);

    var head = el('h2', 'cali-ku-pop__title', title);
    head.id = idBase + '-title';
    scroller.appendChild(head);
    var rating = buildRating(mount, lead, card.parts.length > 1);
    if (rating) {
      var meta = el('div', 'cali-ku-pop__meta');
      meta.appendChild(rating);
      scroller.appendChild(meta);
    }
    scroller.appendChild(el('div', 'cali-ku-pop__price', cardPrice(mount, card)));
    var desc = el('div', 'cali-ku-pop__desc');
    desc.setAttribute('aria-busy', 'true');
    scroller.appendChild(desc);

    var foot = el('div', 'cali-ku-pop__foot');
    var more = el('a', 'cali-ku-pop__more');
    more.href = href;
    more.appendChild(el('span', null, strings.learn_more || title));
    var arrowIcon = document.createElementNS(SVG_NS, 'svg');
    arrowIcon.setAttribute('viewBox', '0 0 16 16');
    arrowIcon.setAttribute('width', '14');
    arrowIcon.setAttribute('height', '14');
    arrowIcon.setAttribute('aria-hidden', 'true');
    arrowIcon.innerHTML = '<path d="M2 8h11M9 4l4 4-4 4" fill="none" stroke="currentColor" stroke-width="1.6"/>';
    more.appendChild(arrowIcon);
    foot.appendChild(more);
    sheet.appendChild(foot);

    function setSlides(urls) {
      while (slides.firstChild) slides.removeChild(slides.firstChild);
      urls.forEach(function (u, i) {
        var li = el('li', 'cali-ku-pop__slide');
        var im = el('img');
        im.alt = '';
        im.decoding = 'async';
        if (i > 0) im.loading = 'lazy';
        im.src = sizedImage(u, 720);
        li.appendChild(im);
        slides.appendChild(li);
      });
      var many = urls.length > 1;
      prev.hidden = next.hidden = counter.hidden = !many;
      updateCount();
    }
    function current() {
      var w = slides.clientWidth || 1;
      return Math.round(slides.scrollLeft / w);
    }
    function updateCount() {
      var n = slides.children.length;
      if (n < 2) return;
      var i = current();
      counter.textContent = (i + 1) + ' / ' + n;
      prev.disabled = i <= 0;
      next.disabled = i >= n - 1;
    }
    function go(dir) {
      var w = slides.clientWidth;
      try {
        slides.scrollTo({ left: (current() + dir) * w, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
      } catch (err) {
        slides.scrollLeft = (current() + dir) * w;
      }
    }
    prev.addEventListener('click', function () { go(-1); });
    next.addEventListener('click', function () { go(1); });
    var countTimer = null;
    slides.addEventListener('scroll', function () {
      if (countTimer) return;
      countTimer = setTimeout(function () { countTimer = null; updateCount(); }, 80);
    }, { passive: true });

    // First paint from what the card already has; the full set replaces it.
    setSlides(variant && variant.img ? [variant.img] : []);

    // Clicks and Escape stay here: Motion would otherwise close the drawer.
    pop.addEventListener('click', function (evt) {
      evt.stopPropagation();
      if (evt.target === backdrop || (evt.target.closest && evt.target.closest('.cali-ku-pop__close'))) closePreview();
    });
    pop.addEventListener('keyup', function (evt) {
      if (evt.key === 'Escape' || evt.keyCode === 27) evt.stopPropagation();
    });
    pop.addEventListener('keydown', function (evt) {
      if (evt.key === 'Escape' || evt.keyCode === 27) {
        evt.preventDefault();
        evt.stopPropagation();
        swallowEscapeUp = true;
        setTimeout(function () { swallowEscapeUp = false; }, 1000);
        closePreview();
        return;
      }
      if (evt.key !== 'Tab') return;
      var f = popFocusables(pop);
      if (!f.length) return;
      var first = f[0];
      var last = f[f.length - 1];
      if (evt.shiftKey && document.activeElement === first) {
        evt.preventDefault();
        last.focus();
      } else if (!evt.shiftKey && document.activeElement === last) {
        evt.preventDefault();
        first.focus();
      }
    });
    more.addEventListener('click', function () {
      track('preview_link', { upsell_offer: card.key, upsell_segment: mount.segmentKey, upsell_arm: mount.arm, upsell_location: mount.location });
    });

    var host = (mount.root.closest && mount.root.closest('.drawer')) || document.body;
    host.appendChild(pop);
    openPop = { node: pop, returnTo: card.els && card.els.title };
    // Next frame, so the opening transition runs.
    requestAnimationFrame(function () {
      pop.classList.add('is-open');
      try { close.focus({ preventScroll: true }); } catch (err) {}
    });
    track('preview', {
      upsell_offer: card.key,
      upsell_segment: mount.segmentKey,
      upsell_position: card.position,
      upsell_arm: mount.arm,
      upsell_location: mount.location
    });

    fetchProduct(lead.handle).then(function (product) {
      if (!openPop || openPop.node !== pop) return;
      var urls = (product.images || []).map(function (u) { return String(u); });
      var featured = null;
      (product.variants || []).forEach(function (v) {
        if (variant && v.id === variant.id && v.featured_image && v.featured_image.src) featured = String(v.featured_image.src);
      });
      var strip = function (u) { return u.replace(/^https?:/, '').split('?')[0]; };
      if (featured) {
        urls = [featured].concat(urls.filter(function (u) { return strip(u) !== strip(featured); }));
      }
      if (urls.length) setSlides(urls.slice(0, 12));
      desc.appendChild(cleanDescription(product.description));
      desc.removeAttribute('aria-busy');
    }).catch(function (err) {
      desc.removeAttribute('aria-busy');
      log('preview fetch failed', err);
    });
  }

  /* ---- impressions ---- */
  function observeImpressions(mount) {
    if (!('IntersectionObserver' in window)) return;
    mount.observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting || mount.root.hidden) return;
        var key = entry.target.getAttribute('data-key');
        var card = null;
        for (var i = 0; i < mount.cards.length; i++) {
          if (mount.cards[i].key === key) { card = mount.cards[i]; break; }
        }
        if (!card) return;
        mount.observer.unobserve(entry.target);
        // cali 2026-10-07 kit upsell: one impression per offer AT a position, in
        // a segment, on a surface (HQ §5 counts impressions per card position);
        // a card that moves after a re-rank is counted again where it now sits.
        var seenKey = [key, card.position, mount.segmentKey || '', mount.location].join('|');
        if (ssList(SS.seen).indexOf(seenKey) !== -1) return;
        ssPush(SS.seen, seenKey);
        track('impression', {
          upsell_offer: key,
          upsell_segment: mount.segmentKey,
          upsell_position: card.position,
          upsell_arm: mount.arm,
          upsell_value: cardCents(card) / 100,
          upsell_location: mount.location
        });
      });
    }, { threshold: 0.5 });
    mount.cards.forEach(function (card) { mount.observer.observe(card.node); });
  }

  function maybeControlImpression(mount) {
    var visibleNow = mount.location === 'cart' || pendingControlOpen;
    if (!visibleNow) return;
    if (mount.location === 'drawer') pendingControlOpen = false;
    if (ssList(SS.control).indexOf(mount.location) !== -1) return;
    ssPush(SS.control, mount.location);
    track('impression', {
      upsell_arm: 'control',
      upsell_segment: mount.segmentKey,
      upsell_location: mount.location
    });
  }

  /* ---- carousel navigation ---- */
  function updateNav(mount) {
    if (!mount.nav) return;
    var t = mount.track;
    var scrollable = t.scrollWidth > t.clientWidth + 2;
    mount.nav.hidden = !scrollable;
    if (!scrollable) return;
    mount.prev.disabled = t.scrollLeft <= 2;
    mount.next.disabled = t.scrollLeft + t.clientWidth >= t.scrollWidth - 2;
  }

  function step(mount, dir) {
    var first = mount.track.querySelector('.cali-ku__card');
    var w = first ? first.getBoundingClientRect().width + 10 : mount.track.clientWidth * 0.8;
    scrollTrackTo(mount, mount.track.scrollLeft + dir * w);
  }

  function flushPending(mount) {
    if (mount.pointerDown || mount.scrolling || mount.adding || !mount.pendingCart) return;
    var cart = mount.pendingCart;
    mount.pendingCart = null;
    render(mount, cart);
  }

  function bindMount(mount) {
    var t = mount.track;
    var down = function () { mount.pointerDown = true; };
    var up = function () {
      if (!mount.pointerDown) return;
      mount.pointerDown = false;
      setTimeout(function () { flushPending(mount); }, 0);
    };
    // cali 2026-10-07 kit upsell: a finger is tracked with touch events, not
    // pointer events. The browser fires pointercancel as soon as a swipe starts
    // panning the track, which used to count as "released" mid-swipe.
    t.addEventListener('touchstart', down, { passive: true });
    window.addEventListener('touchend', up);
    window.addEventListener('touchcancel', up);
    if (window.PointerEvent) {
      t.addEventListener('pointerdown', function (e) { if (e.pointerType !== 'touch') down(); });
      window.addEventListener('pointerup', function (e) { if (e.pointerType !== 'touch') up(); });
    } else {
      t.addEventListener('mousedown', down);
      window.addEventListener('mouseup', up);
    }
    // Momentum scrolling goes on after the finger lifts: wait for it to settle.
    var navTimer = null;
    var idleTimer = null;
    t.addEventListener('scroll', function () {
      closeMenu(mount, false);
      mount.scrolling = true;
      if (idleTimer) clearTimeout(idleTimer);
      idleTimer = setTimeout(function () {
        idleTimer = null;
        mount.scrolling = false;
        flushPending(mount);
      }, SCROLL_IDLE_MS);
      if (navTimer) return;
      navTimer = setTimeout(function () { navTimer = null; updateNav(mount); }, 80);
    }, { passive: true });
    if (mount.prev) mount.prev.addEventListener('click', function () { step(mount, -1); });
    if (mount.next) mount.next.addEventListener('click', function () { step(mount, 1); });
  }

  /* ---- cart sync ---- */
  function renderAll(cart) {
    if (!cart || !cart.items) return;
    lastCart = cart;
    cartReadFailed = false;
    mounts.forEach(function (m) { render(m, cart); });
  }

  function refreshNow() {
    if (refreshTimer) {
      clearTimeout(refreshTimer);
      refreshTimer = null;
    }
    var seq = ++fetchSeq;
    window.fetch(rootPath() + 'cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(function (res) {
        if (!res.ok) throw new Error('cart ' + res.status);
        return res.json();
      })
      .then(function (cart) {
        if (seq !== fetchSeq) return; // a newer read is on its way
        renderAll(cart);
      })
      .catch(function (err) {
        // Cannot know the cart: show nothing rather than something stale, and
        // hand the surface back to the third-party widget (cali 2026-10-07).
        cartReadFailed = true;
        mounts.forEach(function (m) {
          m.awaitingRerank = false;
          hide(m);
        });
        setArmAttribute();
        log('cart read failed', err);
      });
  }

  function scheduleRefresh() {
    if (refreshTimer) clearTimeout(refreshTimer);
    refreshTimer = setTimeout(refreshNow, 100);
  }

  function installNet() {
    // assets/cali-cart-sync.js already nets every cart mutation (fetch/XHR) and
    // broadcasts the fresh cart as `cali:cart`; reuse it instead of wrapping
    // fetch a fourth time. Our own net only when that file is absent.
    if (window.caliCartSync || window.__caliKuNet) return;
    window.__caliKuNet = true;
    var origFetch = window.fetch;
    if (origFetch) {
      window.fetch = function (input) {
        var url = typeof input === 'string' ? input : (input && input.url) || '';
        var promise = origFetch.apply(this, arguments);
        if (CART_MUTATION.test(url)) promise.then(scheduleRefresh, function () {});
        return promise;
      };
    }
    var origOpen = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function (method, url) {
      if (url && CART_MUTATION.test(String(url))) this.addEventListener('load', scheduleRefresh);
      return origOpen.apply(this, arguments);
    };
  }

  function start() {
    if (start.done) return;
    start.done = true;
    var roots = document.querySelectorAll('[data-cali-ku]');
    if (!roots.length || !window.fetch) return;

    Array.prototype.forEach.call(roots, function (root) {
      try {
        var mount = readMount(root);
        mounts.push(mount);
        bindMount(mount);
      } catch (err) {
        try {
          root.hidden = true;
          root.classList.remove('cali-ku--pending');
          root.removeAttribute('style');
        } catch (e) {}
        log('bad mount', err);
      }
    });
    if (!mounts.length) return;

    ensureCss(mounts[0].cfg.css, function (ok) {
      if (!ok) {
        mounts.forEach(function (m) { fail(m, new Error('stylesheet')); });
        return;
      }
      // First paint from the cart Liquid rendered (or a fresher one an event
      // already delivered): no fetch needed.
      var initial = lastCart || mounts[0].cfg.cart;
      if (initial && initial.items) renderAll(initial);
      else refreshNow();
    });

    document.addEventListener('cali:cart', function (evt) {
      var cart = evt.detail && evt.detail.cart;
      if (cart && cart.items) renderAll(cart);
    });
    document.addEventListener('cart:updated', function (evt) {
      var cart = evt.detail && evt.detail.cart;
      if (cart && cart.items) renderAll(cart);
      else scheduleRefresh();
    });
    document.addEventListener('cart:build', scheduleRefresh);
    document.addEventListener('ajaxProduct:added', scheduleRefresh);
    document.addEventListener('cart:open', scheduleRefresh);
    document.addEventListener('drawerOpen.CartDrawer', function () {
      closePreview(); // left open when the cart was closed under it
      pendingControlOpen = true;
      scheduleRefresh();
    });
    installNet();

    var resizeTimer = null;
    window.addEventListener('resize', function () {
      if (resizeTimer) return;
      resizeTimer = setTimeout(function () {
        resizeTimer = null;
        mounts.forEach(function (m) { if (!m.root.hidden) updateNav(m); });
      }, 150);
    });

    window.CaliKitUpsell.debug = { mounts: mounts, refresh: refreshNow };
  }

  /* cali 2026-10-07 kit upsell: out of the first paint. The drawer is closed
     when a page loads, so the drawer carousel starts once the page has loaded
     and the browser is idle, or at once when the cart opens or something is
     added. The cart page (carousel visible, height reserved) starts as soon as
     the document is parsed. DOMContentLoaded runs after every deferred script,
     so cali-cart-sync.js (loaded later in the page) has installed itself. */
  function startNow() {
    var first = !start.done;
    start();
    // The event that woke us was dispatched before start() listened for it.
    if (first && mounts.length) {
      pendingControlOpen = true;
      scheduleRefresh();
    }
  }

  function startWhenIdle() {
    if (start.done) return;
    if (window.requestIdleCallback) window.requestIdleCallback(function () { start(); }, { timeout: 2000 });
    else setTimeout(start, 200);
  }

  function boot() {
    if (document.querySelector('[data-cali-ku][data-cali-ku-location="cart"]')) {
      start();
      return;
    }
    ['drawerOpen.CartDrawer', 'cart:open', 'ajaxProduct:added', 'cart:build'].forEach(function (name) {
      document.addEventListener(name, function wake() {
        document.removeEventListener(name, wake);
        if (!start.done) startNow();
      });
    });
    if (document.readyState === 'complete') startWhenIdle();
    else window.addEventListener('load', startWhenIdle);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
